import { moveItemInArray } from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter,
  HostListener, Input, NgZone, OnDestroy, OnInit, Output, ViewChild
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { cloneDeep, isEqual } from 'lodash';
import { MenuItem, SortMeta } from 'primeng/api';
import { ContextMenuComponent } from '../context-menu/context-menu.component';
import { ScrollableView, Table } from 'primeng/table';
import { BehaviorSubject, Observable, of, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, first, map, switchMap, takeUntil } from 'rxjs/operators';
import { UserProfile } from 'src/app/common/model/user-profile';
import { DataPrintService } from 'src/app/common/service/data-print.service';
import { ToastService } from 'src/app/common/service/toast.service';
import { UserProfileService } from 'src/app/common/service/user-profile.service';
import { FieldType } from 'src/app/settings/model/field-type';
import { Option, ValueField } from 'src/app/settings/model/value-field';
import { StoreService } from 'src/app/store/service/store.service';
import { AdditionalInfoImpl } from 'src/app/vehicles/model/status/additional-info-impl';
import { AllPagesExportService } from '../../../common/service/all-pages-export.service';
import { ColumnDefinition } from '../../model/column-definition';
import { ColumnSetting } from '../../model/column-settings';
import { ColumnSettingsActionName, ColumnSettingsDialogData } from '../../model/column-settings-action';
import { ColumnVisabilityPositionSetting, DataSource, SortBy } from '../../model/data-source';
import { DataSourceCollection } from '../../model/data-source-collection';
import { GroupingHeader } from '../../model/grouping-header';
import { IconMenuButtonEvent } from '../../model/icon-menu-button-event';
import { Operator } from '../../model/operator';
import { RequestCriteria } from '../../model/request-criteria';
import { SummaryFunctions } from '../../model/summary-functions';
import { SummaryObject } from '../../model/summary-object';
import { SummaryType } from '../../model/summary-type';
import { Page, Sort, SortDirection, Transformations } from '../../model/transformations';
import { PanelTriggerDirective } from '../../directive/panel-trigger.directive';
import { FloatingDialogService } from '../../service/floating-dialog/floating-dialog.service';
import { FloatingDialogRef } from '../../service/floating-dialog/floating-dialog-ref';
import { FetchProgressDialogComponent } from '../dialog/fetch-progress-dialog/fetch-progress-dialog.component';
import { ColumnFilterService } from '../../service/column-filter.service';
import { ColumnValuesProvider } from '../../service/column-values-provider.service';
import { DataOptionsConnectorService } from '../../service/data-options-connector.service';
import { TableColumnsService } from '../../service/table-columns.service';
import { DataLoader } from '../../util/data-loader';
import { groupData, GroupedData } from '../../util/data-util';
import { IconMenuCreator } from '../../util/icon-menu-creator';
import { ColumnHiderListComponent } from '../column-options/column-hider-list/column-hider-list.component';
import { ColumnSettingsDialogComponent } from '../dialog/column-settings-dialog/column-settings-dialog.component';
import { SummarySettingsDialogComponent } from '../dialog/summary-settings-dialog/summary-settings-dialog.component';
import { MaterialTableDataSource, RemoteDataType } from './material-table-data-source';
import { ViolationClickEvent } from './parts/content-row-columns/content-row-columns.component';
import { GroupSummaryConfig } from '../../model/group-summary-config';
import { MinimapDirective } from './controllers/minimap.directive';
import { RowRulerDirective } from './controllers/row-ruler.directive';
import { MinimapContext, RowRulerContext } from './controllers/prime-table.context';

@Component({
  selector: 'app-prime-table',
  templateUrl: './prime-table.component.html',
  styleUrls: ['./prime-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: ColumnFilterService, deps: [DataOptionsConnectorService] }]
})
export class PrimeTableComponent<T> implements OnInit, OnDestroy {

  table: Table;
  @ViewChild('table') set setTable(table: Table) {
    const isTableAlreadySet = this.table != null;
    this.table = table;
    if (this.table && !isTableAlreadySet) {
      this.setupTable();
      if (this.pendingInit) {
        const pending = this.pendingInit;
        this.pendingInit = null;
        this.init(pending.dataSource, pending.dataLoader, pending.forceReinit);
      }
    }
  }
  @ViewChild('rowContextMenu') rowContextMenu: ContextMenuComponent;
  @ViewChild('freezeContextMenu') freezeContextMenu: ContextMenuComponent;
  @ViewChild('printMenu') printMenu: PanelTriggerDirective;
  @ViewChild('columnVisibilityPanel') columnVisibilityPanel: PanelTriggerDirective;
  @ViewChild('columnHiderListInline') columnHiderListInline: ColumnHiderListComponent;
  @ViewChild('groupingPanel') groupingPanel: PanelTriggerDirective;
  @ViewChild('sortPanel') sortPanel: PanelTriggerDirective;
  @ViewChild('filterPanel') filterPanel: PanelTriggerDirective;
  @ViewChild('navGoToColumnPanel') navGoToColumnPanel: PanelTriggerDirective;
  @ViewChild('minimapZoomCanvas') minimapZoomCanvasRef: ElementRef<HTMLCanvasElement>;
  @ViewChild('minimapZoomLens') minimapZoomLensRef: ElementRef<HTMLDivElement>;
  @ViewChild('tableCrosshairCanvas') tableCrosshairCanvasRef: ElementRef<HTMLCanvasElement>;
  /**
   * Crosshair canvas is a stable sibling (no *ngIf) — fed into the minimap directive
   * once, right when the directive itself appears (mirrors the old setupMinimap()
   * timing, but driven by Angular's own ViewChild resolution instead of setTimeout).
   */
  @ViewChild(MinimapDirective) set setMinimapDir(dir: MinimapDirective | undefined) {
    this.minimapDir = dir;
    dir?.setCrosshairCanvas(this.tableCrosshairCanvasRef?.nativeElement ?? null);
  }
  minimapDir: MinimapDirective | undefined;
  @ViewChild(RowRulerDirective) rowRulerDir: RowRulerDirective | undefined;

  navPanelOpen = false;
  navColSearchTerm = '';
  groupingSearchTerm = '';
  navColSelectedIndex = -1;
  groupingSelectedIndex = -1;
  sortAddSelectedIndex = -1;
  filterAddSelectedIndex = -1;
  navAtFirstCol = true;
  navAtLastCol = true;
  navAtFirstRow = true;
  navAtLastRow = true;
  private navScrollListener: (() => void) | null = null;
  private navContentResizeObserver: ResizeObserver | null = null;
  private userInputListeners: Array<{ target: EventTarget; type: string; fn: EventListener }> = [];
  private userInputUntil = 0;
  private pointerDownOnTable = false;
  private lastUserScrollTop = -1;
  private skeletonHideTimer: ReturnType<typeof setTimeout> | null = null;
  private skeletonScrollLeft = -1;
  // Sprząta poprzedni watcher zakończenia scrolla (scrollend + fallback timer),
  // gdy nowy scroll startuje zanim poprzedni zdążył się dokończyć.
  private cancelScrollWatch: (() => void) | null = null;

  @Input() selectionType: 'single' | 'multiple' = 'single';
  @Input() remoteDataType: RemoteDataType = RemoteDataType.REST;
  // Cały zbiór danych jest już wczytany do pamięci (np. tryb dzienny) - eksport
  // "Wszystko" pomija fazę pobierania stron i czyta dane bezpośrednio z dataLoadera.
  /**
   * Tabela ma w pamięci całość danych (tryb dzienny), a nie jedną wczytaną stronę. Poza eksportem
   * decyduje to, czy filtr z panelu bocznego dostaje z tabeli listy wartości do wyboru - dlatego
   * rejestracja wisi na secie, a nie na init(): wejście w tryb dzienny i przebudowa tabeli to dwie
   * osobne chwile i kolejność między nimi bywa różna.
   */
  @Input() set allDataPreloaded(value: boolean) {
    this.allDataPreloadedValue = value;
    if (value) this.columnValuesProvider.register(this.dataFilterOptionsProvider);
    else this.columnValuesProvider.unregister(this.dataFilterOptionsProvider);
  }
  get allDataPreloaded(): boolean { return this.allDataPreloadedValue; }
  private allDataPreloadedValue = false;
  @Input() showPagination: boolean = true;
  @Input() paginationSize: number;
  @Input() autoloadData: boolean = true;
  @Input() sortLocally: boolean = false;
  @Input() canSort: boolean = true;
  @Input() expandableDeviceStatusRow: boolean = false;
  @Input() expandableSnvrTask: boolean = false;
  @Input() expandableRowDataSources: DataSourceCollection;
  @Input() filterBuilder: boolean = true;
  @Input() groupingEnabled: boolean = false;
  @Input() groupSummaryConfig: GroupSummaryConfig = {};
  @Input() groupingHeaderEnabled: boolean = false;
  @Input() summaryEnabled: boolean = false;
  @Input() columnFilterEnabled = false;
  @Input() resizeExpandedStatus = false;
  @Input() useRowIdAsPK: boolean;
  @Input() isRandomRowHeight: boolean = false;
  @Input() rowsPerPageOptions: number[] = [25, 50, 100, 200];
  @Input() pageInput: number;
  @Input() tableRowHeight: number = 28;
  @Input() hasMenuOptionsCacheEnabled = false;
  @Input() optionsCellCreator: IconMenuCreator<any>;
  @Input() navButtonsEnabled: boolean = false;
  @Input() showRowNumbers: boolean = false;
  @Input() set showMinimap(value: boolean) {
    this.showMinimapValue = value;
    this.setUpTableScrollHeight();
    // Setup/teardown is now automatic: MinimapDirective's ngOnInit/ngOnDestroy
    // fire exactly when *ngIf="showMinimap && ..." creates/destroys its host.
  }
  get showMinimap(): boolean { return this.showMinimapValue; }
  @Input() set showRowRuler(value: boolean) {
    this.showRowRulerValue = value;
    // Setup/teardown is now automatic — see RowRulerDirective.
  }
  get showRowRuler(): boolean { return this.showRowRulerValue; }
  /**
   * Opcjonalna funkcja mapująca element danych na kolor CSS dla row ruler (linijki wierszy).
   * Gdy podana, row ruler używa pełnego zestawu danych (nie tylko bufora CDK).
   * Przykład: `rowRulerColorFn = item => item.statusColor ? 'rgb(255,100,0)' : undefined`
   */
  @Input() rowRulerColorFn: ((item: T) => string | undefined) | null = null;
  // Liczba pierwszych kolumn danych (bez kolumny "options") przypiętych podczas przewijania poziomego.
  // 0 = przypięta tylko kolumna "options", null = przypinanie wyłączone. Zapisana preferencja
  // użytkownika (TableColumnsService) nadpisuje tę wartość przy init().
  @Input() frozenColumnsCount: number | null = null;
  // Bufory renderowania wirtualnego scrolla (px). Większe wartości = mniej "pustych"
  // wierszy przy szybkim przewijaniu, kosztem liczby wierszy trzymanych w DOM.
  @Input() minBufferPx: number = 100;
  @Input() maxBufferPx: number = 200;
  // Recykling widoków wierszy w cdkVirtualFor (opt-in). PrimeNG wyłącza go na sztywno
  // ([cdkVirtualForTemplateCacheSize]="0" w szablonie TableBody), przez co każdy wiersz
  // wjeżdżający w viewport jest budowany od zera - to główny koszt "pustki" przy scrollu.
  // Wartość > 0 = tyle widoków trafia do cache'u i przy scrollu jest przepinanych na nowe
  // wiersze (rebind) zamiast tworzonych. 0 = zachowanie PrimeNG. Widoki w cache'u trzymają
  // odłączony DOM i stan dyrektyw, więc włączać tam, gdzie szablon wiersza jest bezstanowy.
  @Input() virtualViewCacheSize: number = 0;
  // Skeleton na niewyrenderowanych obszarach podczas przewijania (opt-in)
  @Input() scrollSkeletonEnabled: boolean = false;
  // Skeleton na całą tabelę na czas ładowania danych (chowa też komunikat o braku danych)
  @Input() set skeletonWhileLoading(value: boolean) {
    this.skeletonLoadingActive = !!value;
    this.applyLoadingSkeleton();
  }
  private skeletonLoadingActive = false;
  @Output() rowClicked = new EventEmitter<T>();
  @Output() rowDoubleClicked = new EventEmitter<T>();
  @Output() rowButtonClicked = new EventEmitter<IconMenuButtonEvent<T>>();
  @Output() rowSelectionChanged = new EventEmitter<T[]>();
  @Output() violationClicked = new EventEmitter<ViolationClickEvent<T>>();
  @Output() rowFocus = new EventEmitter<T>();
  // Scroll wykonany przez użytkownika (programowe scrolle są wytłumiane) - emituje scrollTop.
  // Emisja poza strefą Angulara - odbiorca sam decyduje, czy potrzebuje zone.run().
  @Output() userScroll = new EventEmitter<number>();


  displayedColumns: ColumnDefinition[] = [];
  columnVisibilitySettings: ColumnVisabilityPositionSetting[] = [];
  pendingColumnVisibilitySettings: ColumnVisabilityPositionSetting[] = [];
  columnVisibilityFormValid = true;
  isColumnListDragging = false;
  private optionsColumn: ColumnDefinition;

  contentMenuOptions: MenuItem[] = [];
  buildMenuOptions: (_data: T | T[] | any | any[], _userProfile: UserProfile) => MenuItem[] = null;
  selectedElement: T | T[];
  printScope: PrintScope = 'page';
  currentDataCount = 0;
  readonly LARGE_DATA_THRESHOLD = 5000;
  contextMenuSelection: T | T[] | null;
  matDataSource: MaterialTableDataSource<T>;
  dataSource: DataSource;
  private exportProgressRef: FloatingDialogRef | null = null;
  private dataLoader: DataLoader<T>;
  private dataSubscription?: Subscription;
  private dataCountSubscription?: Subscription;
  private dataErrorSubscription?: Subscription;
  private unsubscribeChanges$ = new Subject();
  private dataSubject: BehaviorSubject<Array<T | GroupedData<T>>>;
  private dataFilterOptionsCache = new Map<string, Option[]>();
  private dataFilterOptionsVersion: number | null = null;
  dataSourceDataObj: Observable<Array<T | GroupedData<T>>> | null;
  // Liczba faktycznie renderowanych wierszy danych - przy grupowaniu pomija wiersze
  // zwiniętych grup (i same nagłówki grup), więc odpowiada temu, co użytkownik naprawdę widzi.
  private readonly visibleRowCountSubject = new BehaviorSubject<number>(0);
  readonly visibleRowCount$: Observable<number> = this.visibleRowCountSubject.asObservable();
  private resizeSubject = new Subject<number>();
  paginatorVisible: boolean = false;
  /** Czy użytkownik ukrył pasek paginacji dla tej tabeli (ustawienie per-tabela, przycisk "Widok"). */
  paginationUserHidden = false;
  private containerHeight = 150;
  tableScrollHeight = '150px';
  dataPrimaryKey: string = null;
  groupBy: string | null = null;
  cachedGroupByField: ValueField | null = null;
  expandedGroups = new Set<string>();
  private groupedData: GroupedData<T>[] | null = null;
  private resizeObserver: ResizeObserver;
  displayTable: boolean = true;
  private tempPageFirst?: number;
  private tempPageRows?: number;
  groupingHeaders: GroupingHeader[];
  summarySubscription: Subscription;
  displayFooter = false;
  summaryObject: SummaryObject;
  private summaryType: SummaryType | null = null;
  isSummaryLoading: boolean = false;
  extendedContainerWidth: number = 0;
  multiSortMeta: SortMeta[] = [];
  appliedSortMeta: SortMeta[] = [];
  private tempMultisortMeta: SortMeta[] = [];
  // Bufor na wywołanie init() trafiające w okno, gdy #table jest chwilowo odmontowana
  // (displayTable=false w setupTableViewRefresh() z poprzedniego cyklu jeszcze nie wróciło
  // do true) - np. przy błyskawicznym dwukrotnym przełączeniu trybu wolny/dzienny SAKP Odjazdy z sidebar.
  private pendingInit: { dataSource: DataSource; dataLoader: DataLoader<T>; forceReinit: boolean } | null = null;
  static readonly DEFAULT_COLUMN_WIDTH = 150;
  static readonly OPTIONS_COLUMN_WIDTH = 30;
  static readonly MIN_COLUMN_WIDTH = 40;
  static readonly MAX_FROZEN_COLUMNS = 8;
  // PrimeNG 12 ScrollableView.onHeaderScroll zakłada tryb bez virtual scroll i sięga po
  // scrollBodyViewChild, które w trybie virtualScroll nigdy nie istnieje (renderowany jest
  // cdk-virtual-scroll-viewport). Prototyp łatamy jednokrotnie — patrz patchScrollableViewHeaderScroll().
  private static scrollableViewHeaderScrollPatched = false;
  // strefa przypięta nie może zająć więcej niż ta część szerokości kontenera —
  // reszta musi zostać na przewijaną treść, inaczej przewijanie poziome nic nie pokazuje
  private static readonly FROZEN_ZONE_MAX_VIEWPORT_RATIO = 0.6;
  private lastFrozenOffsetsKey = '';
  freezeMenuItems: MenuItem[] = [];
  // stan oczekujący z panelu "Widoczność kolumn" — stosowany dopiero przy zapisie
  pendingFrozenColumnsCount: number | null = null;
  private tableContainerWidth = 0;
  private columnWidthsCache: {
    columns: ColumnDefinition[]; settingsVersion: number; widths: Map<string, number>;
  } | null = null;
  private columnSettingsVersion = 0;
  sortReady: boolean = false;
  selectableSort: boolean = false;
  sortOverlayItems: Array<{ field: string; order: 1 | -1; active: boolean; appliedOrder: 1 | -1 | null; appliedIndex: number | null }> = [];
  sortAddSearch: string = '';
  isSortListDragging = false;
  private skipResolveSort = false;
  filterOverlayItems: FilterOverlayItem[] = [];
  filterAddSearch: string = '';

  private static readonly MINIMAP_HEIGHT_MIN = 40;
  private static readonly MINIMAP_HEIGHT_DEFAULT = 64;
  private showMinimapValue = false;
  /** Widoczność minimap sterowana przez użytkownika (przycisk "Widok") */
  minimapUserVisible = false;
  /** Widoczność paska filtrów sterowana przez użytkownika (przycisk "Widok") */
  columnFilterVisible = true;

  // ─── Row ruler — linijka wierszy (overview ruler, flex-sibling canvas) ──
  private showRowRulerValue = false;
  rowRulerVisible = false;

  // ─── Template-bound minimap state ────────────────────────────────────────
  minimapHeight = PrimeTableComponent.MINIMAP_HEIGHT_DEFAULT;
  minimapIndicatorLeft = 0;
  minimapIndicatorWidth = 0;
  minimapDragActive = false;
  minimapResizeActive = false;

  // ─── Zoom lens minimap (środkowy przycisk) ───────────────────────────────────
  showMinimapZoom = false;
  minimapZoomLeft = 0;
  minimapZoomTop = 0;
  minimapZoomWidth = 480;
  minimapZoomHeight = 256;
  /** true gdy użytkownik ręcznie przeciągnął okno zoom (drag handle) — zmienia kolor obramowania */
  get minimapZoomDragged(): boolean { return this.minimapDir?.zoomManuallyPositioned ?? false; }
  /** true gdy okno zoom jest przypięte (przycisk pinezki) — nie zamyka się samo na klik poza nim */
  get minimapZoomPinned(): boolean { return this.minimapDir?.zoomPinned ?? false; }

  // ─── Kontekst przekazywany do dyrektyw [appMinimap] / [appRowRuler] ────────
  minimapContext: MinimapContext;
  rowRulerContext: RowRulerContext;

  get changedSortCount(): number {
    const appliedMap = new Map(this.appliedSortMeta.map(m => [m.field, m.order]));
    const pendingMap = new Map(this.multiSortMeta.map(m => [m.field, m.order]));
    let count = 0;
    for (const [field, order] of pendingMap) {
      if (!appliedMap.has(field) || appliedMap.get(field) !== order) count++;
    }
    for (const field of appliedMap.keys()) {
      if (!pendingMap.has(field)) count++;
    }
    return count;
  }

  set selection(selection: T | T[]) {
    if (!isEqual(this.selectedElement, selection)) {
      this.selectedElement = selection;
      if (Array.isArray(this.selectedElement)) this.rowSelectionChanged.emit(this.selectedElement);
      else this.rowSelectionChanged.emit(this.selectedElement == null ? [] : [this.selectedElement]);
    }
  }

  get selection(): T | T[] {
    if (!this.selectedElement) return [];
    return Array.isArray(this.selectedElement) ? this.selectedElement : [this.selectedElement];
  }

  get visibleColumnsCount(): number {
    return this.displayedColumns?.filter(c => c.name !== 'options').length ?? 0;
  }

  get pendingVisibleColumnsCount(): number {
    return this.pendingColumnVisibilitySettings.filter(v => v.isVisible).length;
  }

  get pendingTotalColumnsCount(): number {
    return this.pendingColumnVisibilitySettings.length;
  }

  get captionVisible(): boolean {
    // FIXME czy wszędzie widoczne?.
    return this.displayedColumns?.length > 0;
  }

  get sortOverlayActiveCount(): number {
    return this.sortOverlayItems.filter(i => i.active).length;
  }

  get sortableColumnsNotInOverlay(): ColumnDefinition[] {
    const overlayFields = new Set(this.sortOverlayItems.map(i => i.field));
    return this.displayedColumns
      .filter(c => c.sortable && c.name !== 'options' && !overlayFields.has(c.name))
      .sort((a, b) => this.compareColumnTitles(a, b));
  }

  private compareColumnTitles = (a: ColumnDefinition, b: ColumnDefinition): number => {
    const titleA = (this.translate.instant(a.title) || a.title).toLowerCase();
    const titleB = (this.translate.instant(b.title) || b.title).toLowerCase();
    return titleA.localeCompare(titleB, this.translate.currentLang || 'pl');
  };

  get filteredColumnsToAdd(): ColumnDefinition[] {
    const phrase = (this.sortAddSearch ?? '').toLowerCase().trim();
    const available = this.sortableColumnsNotInOverlay;
    if (!phrase) return available;
    return available.filter(c => {
      const title = (this.translate.instant(c.title) || c.title).toLowerCase();
      return title.includes(phrase);
    });
  }

  get filterableColumns(): ColumnDefinition[] {
    return this.displayedColumns?.filter(c => c.name !== 'options') ?? [];
  }

  get columnsNotInFilterOverlay(): ColumnDefinition[] {
    const overlayFields = new Set(this.filterOverlayItems.map(i => i.field));
    return this.filterableColumns
      .filter(c => !overlayFields.has(c.name))
      .sort((a, b) => this.compareColumnTitles(a, b));
  }

  get filteredColumnsToAddFilter(): ColumnDefinition[] {
    const phrase = (this.filterAddSearch ?? '').toLowerCase().trim();
    const available = this.columnsNotInFilterOverlay;
    if (!phrase) return available;
    return available.filter(c => {
      const title = (this.translate.instant(c.title) || c.title).toLowerCase();
      return title.includes(phrase);
    });
  }

  get filterOverlayActiveCount(): number {
    return this.filterOverlayItems.filter(i => i.active).length;
  }

  get filterOverlayAllActive(): boolean {
    return this.filterOverlayItems.length > 0 && this.filterOverlayItems.every(i => i.active);
  }

  get filterOverlayHasChanges(): boolean {
    return this.filterOverlayItems.some(item => {
      if (item.appliedValue === null) return item.active;
      if (!item.active) return true;
      return !isEqual(item.value, item.appliedValue);
    });
  }

  get appliedFilterCount(): number {
    return this.filterableColumns.filter(c => !!this.columnFilterService.getSavedValue(c.name)).length;
  }

  constructor(private storeService: StoreService, private dataOptionsConnectorService: DataOptionsConnectorService, private toast: ToastService,
    private changeDetector: ChangeDetectorRef, private matDialog: MatDialog, private elementRef: ElementRef,
    private translate: TranslateService, private tableColumnsService: TableColumnsService, private zone: NgZone,
    private columnFilterService: ColumnFilterService, private columnValuesProvider: ColumnValuesProvider,
    private userProfileService: UserProfileService, public printService: DataPrintService,
    public exportService: AllPagesExportService, private floatingDialogService: FloatingDialogService) {
    this.optionsColumn = new ColumnDefinition();
    Object.assign(this.optionsColumn, {
      name: 'options',
      visible: true,
      sortable: false
    });
  }

  ngOnInit(): void {
    // Przywróć globalne preferencje widoku (minimap/ruler/filterBar on|off per user).
    // Wykonujemy synchronicznie, zanim Angular wykona pierwszą detekcję zmian, żeby
    // *ngIf minimap/ruler w szablonie zobaczył prawidłowe wartości już przy pierwszym
    // renderze (MinimapDirective/RowRulerDirective inicjalizują się same w ngOnInit).
    const prefs = this.tableColumnsService.getViewPrefs();
    if (prefs.minimap !== undefined) this.minimapUserVisible = prefs.minimap;
    if (prefs.ruler !== undefined) this.rowRulerVisible = prefs.ruler;
    if (prefs.filterBar !== undefined) this.columnFilterVisible = prefs.filterBar;

    this.paginationSize = this.rowsPerPageOptions[0];

    // Postęp eksportu/wydruku w FloatingDialog (CDK) — otwierany/zamykany reaktywnie wg isBusy$.
    // Wspólne id => nawet przy wielu tabelach jest jedna instancja (dedup w serwisie).
    this.exportService.isBusy$
      .pipe(distinctUntilChanged(), takeUntil(this.unsubscribeChanges$))
      .subscribe(busy => {
        if (busy) {
          this.exportProgressRef = this.floatingDialogService.open(FetchProgressDialogComponent, {
            id: 'export-progress',
            modal: true,
            closable: false,
            maximizable: false,
            resizable: false,
            closeOnEscape: false,
            fitContent: true
          });
        } else {
          this.exportProgressRef?.close();
          this.exportProgressRef = null;
        }
      });

    this.dataOptionsConnectorService.activeFilters$
      .pipe(takeUntil(this.unsubscribeChanges$))
      .subscribe(() => {
        if (this.table?.first)
          this.table.first = 0;
        this.updateDataSourceData();
        this.refreshSummary();
      });

    this.subscribeResizeSubject();
    this.resizeObserver = new ResizeObserver((observerEntries: ResizeObserverEntry[]) => {
      const rect = observerEntries.length ? observerEntries[0].contentRect : null;
      if (rect?.height || rect?.width) {
        this.zone.run(() => {
          if (rect.width)
            this.tableContainerWidth = Math.floor(rect.width);
          if (rect.height)
            this.resizeSubject.next(Math.floor(rect.height));
        });
      }
    });
    this.resizeObserver.observe(this.elementRef.nativeElement.parentElement);

    this.zone.runOutsideAngular(() => {
      const el = this.elementRef.nativeElement;
      const handler = (event: MouseEvent): void => {
        const className = 'no-text-select';
        if (event.shiftKey || event.ctrlKey) {
          if (!document.body.classList.contains(className))
            document.body.classList.add(className);
        } else {
          document.body.classList.remove(className);
        }
      };
      ['mousemove', 'mouseup', 'mousedown', 'click'].forEach(type => el.addEventListener(type, handler));
      // Scroll nie bąbelkuje, ale da się go przechwycić w fazie capture — dzięki temu nasłuch
      // przeżywa odtwarzanie p-table przez *ngIf (setupTableViewRefresh).
      el.addEventListener('scroll', this.bodyScrollSyncListener, { capture: true, passive: true });
      // Poziome przewijanie klawiaturą (←/→) obsługujemy sami: patrz komentarz przy horizontalKeyScrollListener.
      el.addEventListener('keydown', this.horizontalKeyScrollListener, { capture: true });
    });

    // ── Kontekst dla dyrektyw minimap / row ruler ──────────────────────────
    // NgZone i TableColumnsService są wstrzykiwane bezpośrednio do dyrektyw (DI) —
    // nie są już częścią kontekstu.
    this.minimapContext = {
      getScrollContainer: () => this.getTableScrollWrapper(),
      getTableHost: () => this.table?.el?.nativeElement as HTMLElement | undefined,
      getNavRowHeight: () => this.getNavRowHeight(),
      getDisplayedColumns: () => this.displayedColumns,
      getDataSourceName: () => this.dataSource?.name ?? null,
      setMinimapHeight: (h) => { this.minimapHeight = h; },
      setMinimapDragging: (v) => { this.minimapDragActive = v; },
      setMinimapResizing: (v) => { this.minimapResizeActive = v; },
      setMinimapIndicator: (l, w) => { this.minimapIndicatorLeft = l; this.minimapIndicatorWidth = w; },
      setShowZoom: (v) => this.zone.run(() => {
        this.showMinimapZoom = v;
        if (v) {
          setTimeout(() => {
            const lensEl = this.minimapZoomLensRef?.nativeElement ?? null;
            // Wynieś okno zoom poza <mat-drawer-content> — ten element ustawia
            // własny stacking context (position:relative + z-index), który
            // przycina WSZYSTKIE potomki, także position:fixed, poniżej
            // sąsiedniego panelu bocznego (mat-drawer-side), niezależnie od
            // z-index samego okienka. Dołączając je bezpośrednio do <body>
            // (gdzie i tak żyje klasa motywu jasny/ciemny — zmienne CSS
            // nadal działają poprawnie) unikamy tego ograniczenia bez
            // dotykania globalnych stylów Material (patrz material-styles.scss).
            if (lensEl && lensEl.parentElement !== document.body) {
              document.body.appendChild(lensEl);
            }
            this.minimapDir?.setZoomCanvas(this.minimapZoomCanvasRef?.nativeElement ?? null);
            this.minimapDir?.setZoomLensEl(lensEl);
          });
        } else {
          this.minimapDir?.setZoomLensEl(null);
        }
      }),
      setZoomGeometry: (l, t, w, h) => {
        this.minimapZoomLeft = l; this.minimapZoomTop = t;
        this.minimapZoomWidth = w; this.minimapZoomHeight = h;
      },
      scheduleTableHeightRecalc: () => this.setUpTableScrollHeight(),
      getInitialColumnWidth: (name) => this.getInitialColumnWidth(name),
      getMinimapHeight: () => this.minimapHeight
    };

    this.rowRulerContext = {
      getScrollContainer: () => this.getTableScrollWrapper(),
      getTableHost: () => this.table?.el?.nativeElement as HTMLElement | undefined,
      getNavRowHeight: () => this.getNavRowHeight(),
      getDisplayedColumns: () => this.displayedColumns,
      getDataArray: () => this.table?.value ?? [],
      getDataSourceName: () => this.dataSource?.name ?? null,
      rowRulerColorFn: () => this.rowRulerColorFn as ((item: unknown) => string | undefined) | null
    };
  }

  private scrollSyncLastLeft = -1;

  // Synchronizacja POZIOMEGO przewijania nagłówka i stopki z ciałem tabeli. Nasłuch scroll działa w fazie
  // capture na hoście, więc łapie KAŻDE zdarzenie scroll ciała (również wywołane klawiaturą) i przeżywa
  // odtwarzanie p-table przez *ngIf.
  //
  // Synchronizacja jest SYNCHRONICZNA, bez pętli i bez odroczonej pracy (jak natywnie w PrimeNG): każde
  // zdarzenie scroll od razu przepisuje bieżący scrollLeft ciała na nagłówek/stopkę (scrollSyncLastLeft
  // służy tylko do odfiltrowania zdarzeń pionowych). Świadomie NIE utrzymujemy tu żadnej pętli
  // requestAnimationFrame — pętla rAF potrafiła się rozbiegać (np. przy resize okna, gdy viewport przelicza
  // geometrię i sypie zdarzeniami scrolla, a w trybie frozen ustawienie header.scrollLeft odbija się przez
  // PrimeNG onHeaderScroll z powrotem na body), blokując wątek główny. Prosty, bezpętlowy handler nie ma
  // czego rozbiegać.
  private bodyScrollSyncListener = (event: Event): void => {
    const target = event.target as HTMLElement;
    if (!target?.classList?.contains('p-datatable-virtual-scrollable-body') && !target?.classList?.contains('p-datatable-scrollable-body'))
      return;
    const left = target.scrollLeft;
    // Zdarzenie scroll odpala się też przy przewijaniu PIONOWYM (wirtualny scroll) — wtedy scrollLeft się
    // nie zmienia, więc nie ma czego synchronizować.
    if (left === this.scrollSyncLastLeft)
      return;
    this.scrollSyncLastLeft = left;
    this.syncHeaderToScrollLeft(left);
  };

  private getScrollableBody(): HTMLElement | null {
    const root = this.elementRef.nativeElement as HTMLElement;
    return (root.querySelector('.p-datatable-virtual-scrollable-body')
      || root.querySelector('.p-datatable-scrollable-body'));
  }

  // Przepisuje poziomy offset ciała na nagłówek i stopkę.
  private syncHeaderToScrollLeft(scrollLeft: number): void {
    const root = this.elementRef.nativeElement as HTMLElement;
    const header = root.querySelector('.p-datatable-scrollable-header') ;
    const footer = root.querySelector('.p-datatable-scrollable-footer') ;
    if (this.frozenCellsTotal > 0) {
      // Przypinanie: komórki używają position: sticky, które wymaga margin-left = 0 (wymuszone w CSS .pt-frozen),
      // więc nagłówek/stopkę przesuwamy realnym scrollLeft kontenera (overflow:hidden nadal daje się przewinąć programowo).
      if (header && header.scrollLeft !== scrollLeft) header.scrollLeft = scrollLeft;
      if (footer && footer.scrollLeft !== scrollLeft) footer.scrollLeft = scrollLeft;
    } else {
      // Bez przypinania: przesuwamy boks nagłówka/stopki ujemnym margin-left (jak PrimeNG), ale bezwarunkowo.
      const headerBox = header?.querySelector<HTMLElement>('.p-datatable-scrollable-header-box') ;
      const footerBox = footer?.querySelector<HTMLElement>('.p-datatable-scrollable-footer-box') ;
      if (headerBox) headerBox.style.marginLeft = -scrollLeft + 'px';
      if (footerBox) footerBox.style.marginLeft = -scrollLeft + 'px';
    }
  }

  // Poziome przewijanie klawiaturą (←/→) obsługujemy sami, zamiast pozwolić przeglądarce.
  //
  // Diagnostyka pokazała, że podczas przewijania wątek główny jest zagłodzony (rAF/scroll odpalają się
  // co ~500 ms, nie ~16 ms — ciężki render wirtualnego scrolla / skeleton / change detection). Domyślne
  // przewijanie klawiszem jest PŁYNNIE animowane na wątku KOMPOZYTORA, więc ciało suwa się gładko, a
  // synchronizacja nagłówka (na wątku głównym: header.scrollLeft / margin-left) nie nadąża — nagłówek
  // szarpie się kilka klatek za danymi. Żadna synchronizacja z wątku głównego (scroll event, rAF) tego
  // nie naprawi, bo wątek nie dostaje klatek.
  //
  // Rozwiązanie: anulujemy domyślne (animowane) przewijanie i wykonujemy NATYCHMIASTOWY skok scrollLeft
  // ciała ORAZ synchronizację nagłówka w tym samym, synchronicznym handlerze. Bez animacji kompozytora
  // nie ma okna, w którym nagłówek mógłby się rozjechać z danymi — oba przeskakują razem i pozostają
  // zsynchronizowane nawet jeśli zaraz potem wątek główny się zablokuje na render.
  private horizontalKeyScrollListener = (event: KeyboardEvent): void => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')
      return;
    if (event.ctrlKey || event.altKey || event.metaKey || event.shiftKey)
      return;
    const target = event.target as HTMLElement | null;
    const tag = target?.tagName;
    // Nie przejmujemy strzałek w polach edycyjnych (kursor w input/textarea/select/contenteditable).
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable)
      return;
    const host = this.elementRef.nativeElement as HTMLElement;
    if (!target || !host.contains(target))
      return;
    const body = this.getScrollableBody();
    if (!body)
      return;
    const max = body.scrollWidth - body.clientWidth;
    if (max <= 0)
      return; // brak poziomego przewijania — nie przechwytuj (strzałki mogą służyć do czego innego)
    const step = 60;
    const next = Math.max(0, Math.min(max, body.scrollLeft + (event.key === 'ArrowLeft' ? -step : step)));
    if (next === body.scrollLeft)
      return;
    event.preventDefault();
    body.scrollLeft = next; // natychmiastowy skok — bez animacji kompozytora
    this.scrollSyncLastLeft = next;
    this.syncHeaderToScrollLeft(next); // nagłówek/stopka w tym samym ticku, gwarantowana zgodność
  };

  // Offsety sticky (skumulowane szerokości poprzedzających kolumn) jako zmienne CSS na hoście.
  private updateFrozenLeftOffsets(widths: Map<string, number>): void {
    const offsets: number[] = [];
    let cumulated = 0;
    for (const column of this.displayedColumns ?? []) {
      if (offsets.length > PrimeTableComponent.MAX_FROZEN_COLUMNS)
        break;
      offsets.push(cumulated);
      cumulated += column.name === 'options'
        ? PrimeTableComponent.OPTIONS_COLUMN_WIDTH
        : (widths.get(column.name) ?? PrimeTableComponent.DEFAULT_COLUMN_WIDTH);
    }
    const key = offsets.join(',');
    if (key === this.lastFrozenOffsetsKey)
      return;
    this.lastFrozenOffsetsKey = key;
    const style = (this.elementRef.nativeElement as HTMLElement).style;
    offsets.forEach((offset, index) => style.setProperty('--pt-frozen-left-' + (index + 1), offset + 'px'));
  }

  resizeExtendedContainer(entry: ResizeObserverEntry): void {
    if (entry.contentRect.width > 40)
      this.extendedContainerWidth = entry.contentRect.width - 40;
    else
      this.extendedContainerWidth = 0;
  }

  // Stała referencja, żeby wyrejestrowanie trafiło dokładnie w tę tabelę (patrz ColumnValuesProvider).
  private readonly dataFilterOptionsProvider = (columnName: string): Option[] => this.getDataFilterOptions(columnName);

  ngOnDestroy(): void {
    this.columnValuesProvider.unregister(this.dataFilterOptionsProvider);
    // MinimapDirective/RowRulerDirective clean up themselves via ngOnDestroy,
    // triggered by Angular when their *ngIf-guarded host is removed (which
    // happens naturally when this component itself is destroyed).
    this.elementRef.nativeElement.removeEventListener('scroll', this.bodyScrollSyncListener, { capture: true } as EventListenerOptions);
    this.elementRef.nativeElement.removeEventListener('keydown', this.horizontalKeyScrollListener, { capture: true } as EventListenerOptions);
    this.removeNavScrollListener();
    this.removeUserScrollListener();
    this.cancelScrollWatch?.();
    if (this.summarySubscription)
      this.summarySubscription.unsubscribe();
    this.unsubscribeChanges$.next();
    this.unsubscribeChanges$.complete();
    this.exportProgressRef?.close();
    this.exportProgressRef = null;
    this.resizeObserver.unobserve(this.elementRef.nativeElement.parentElement);
    if (this.columnResizeState) {
      document.removeEventListener('pointermove', this.columnResizeState.move);
      document.removeEventListener('pointerup', this.columnResizeState.up);
      document.removeEventListener('pointercancel', this.columnResizeState.up);
      this.columnResizeState.helper.remove();
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      this.columnResizeState = null;
    }
    this.matDataSource?.disconnect();
    if (this.columnFilterEnabled)
      this.columnFilterService.ngOnDestroy();
  }

  init(dataSource: DataSource, dataLoader: DataLoader<T>, forceReinit: boolean = true): void {
    if (!this.table) {
      // #table jest właśnie odmontowana przez trwający cykl setupTableViewRefresh()
      // (displayTable=false). Odłóż init() do momentu, gdy ViewChild znów się ustawi,
      // zamiast wywalać się na this.table.multiSortMeta poniżej.
      this.pendingInit = { dataSource, dataLoader, forceReinit };
      return;
    }
    if (forceReinit || !this.matDataSource) {
      this.matDataSource?.disconnect();
      this.dataSource = dataSource;
      this.resetSort();
      if (dataSource.sortBy?.length > 0) {
        this.table.multiSortMeta = dataSource.sortBy.map(d => d.toMultiSortMeta());
      }

      this.dataLoader = dataLoader;
      this.dataFilterOptionsCache.clear();
      this.dataFilterOptionsVersion = null;
      this.columnFilterService.registerDataOptionsProvider(name => this.getDataFilterOptions(name));

      this.groupBy = this.tableColumnsService.getGroupingColumn(dataSource.name);
      const storedFrozenCount = this.tableColumnsService.getFrozenColumnsCount(dataSource.name);
      if (storedFrozenCount != null)
        this.frozenColumnsCount = storedFrozenCount;
      this.paginationUserHidden = this.tableColumnsService.getPaginationHidden(dataSource.name);
      this.dataSource.groupBy = this.groupBy;
      this.cachedGroupByField = this.groupBy ? this.findFieldByName(this.groupBy) : null;
      this.setupTableViewRefresh();
      this.tempPageRows = null;

      this.setColumns(this.getCurrentColumnsSettings());

      const primaryKeyField = this.dataSource.fields.find(field => field.valueField.primaryKey);
      if (primaryKeyField)
        this.dataPrimaryKey = primaryKeyField.column.name;

      this.dataCountSubscription?.unsubscribe();
      this.dataSubscription?.unsubscribe();
      this.matDataSource = new MaterialTableDataSource<T>(this.storeService, this.getTransformations.bind(this), this.remoteDataType, this.sortLocally);
      this.createDataSourceData();
      this.dataCountSubscription = this.matDataSource.dataCount$
        .pipe(takeUntil(this.unsubscribeChanges$))
        .subscribe(count => {
          this.currentDataCount = count;
          this.paginatorVisible = this.showPagination && !this.paginationUserHidden;
          this.setUpTableScrollHeight();
        });

      this.dataErrorSubscription?.unsubscribe();

      if (this.dataLoader.error$) {
        this.dataErrorSubscription = this.dataLoader.error$
          .pipe(
            takeUntil(this.unsubscribeChanges$),
            filter(error => error),
            switchMap(error => {
              if (error.errors && Object.values(error.errors).length)
                return this.translate.get(Object.values<string>(error.errors))
                  .pipe(map(translations => Object.values(translations).join(' ')));
              else
                return of(null);
            })
          )
          .subscribe(errorMessage => {
            if (errorMessage)
              this.toast.showErrorToast(errorMessage);
            else
              this.toast.showErrorToast('serverConnectionError');
          });
      }

      this.matDataSource.loading$
        .pipe(takeUntil(this.unsubscribeChanges$))
        .subscribe(loading => this.dataOptionsConnectorService.updateLoading(loading));

      this.dataOptionsConnectorService.cancel$
        .pipe(takeUntil(this.unsubscribeChanges$))
        .subscribe(() => this.cancelLoading());

      if (this.remoteDataType == RemoteDataType.REST)
        this.matDataSource.setDataLoader(dataLoader);

      if (this.autoloadData) {
        if (this.dataLoader.error$) {
          this.dataLoader.error$.pipe(first(), filter(error => !error)).subscribe(() => this.loadData());
        } else {
          this.loadData();
        }
      }
      this.changeDetector.detectChanges();
    }
  }

  private setupTable(): void {
    if (this.showPagination) {
      this.table.first = this.tempPageFirst || 0;
      this.table.rows = this.tempPageRows || this.paginationSize;
    }
    this.table.multiSortMeta = this.tempMultisortMeta;
    this.table.sortMultiple();
    this.tempPageFirst = null;
    this.tempPageRows = null;
    this.tempMultisortMeta = null;
    if (this.remoteDataType == RemoteDataType.WEBSOCKET) {
      this.matDataSource?.data$.pipe(first()).subscribe(data => this.updateTableData(data));
    } else if (this.remoteDataType == RemoteDataType.REST && !this.table.multiSortMeta) {
      this.loadData();
    }
    if (this.navPanelOpen) {
      setTimeout(() => this.setupNavScrollListener());
    }
    PrimeTableComponent.patchScrollableViewHeaderScroll();
    setTimeout(() => {
      this.setupUserScrollListener();
      this.applyVirtualViewRecycling();
    });
    this.changeDetector.detectChanges();
    // MinimapDirective/RowRulerDirective self-initialize (with an internal bounded
    // retry until getScrollContainer()/this.table is ready) — no manual kick needed.
  }

  // Dyrektywa cdkVirtualFor nie jest osiągalna publicznym API PrimeNG - viewport trzyma ją
  // w _forOf, a jej setter cdkVirtualForTemplateCacheSize przestawia viewCacheSize strategii
  // recyklingu (CDK 12: scrolling.js). Jak setupUserScrollListener: viewport potrafi pojawić
  // się później niż setupTable(), stąd ponawianie prób.
  private applyVirtualViewRecycling(attempt: number = 0): void {
    if (this.virtualViewCacheSize <= 0) return;
    const forOf = (this.table as any)?.scrollableViewChild?.virtualScrollBody?._forOf;
    if (!forOf) {
      if (attempt < 20) setTimeout(() => this.applyVirtualViewRecycling(attempt + 1), 250);
      return;
    }
    forOf.cdkVirtualForTemplateCacheSize = this.virtualViewCacheSize;
  }

  // Łatka na błąd PrimeNG 12: ScrollableView.onHeaderScroll bezwarunkowo ustawia
  // scrollBodyViewChild.nativeElement.scrollLeft, ale w trybie virtualScroll ten ViewChild
  // nie istnieje (renderowany jest cdk-virtual-scroll-viewport → tylko virtualScrollBody).
  // Nasza synchronizacja nagłówka (syncHeaderToScrollLeft — przy aktywnym przypinaniu ustawia
  // header.scrollLeft) wyzwala natywny event scroll i onHeaderScroll → TypeError. Podmieniamy metodę na
  // prototypie eksportowanej klasy (jedna wspólna klasa, więc jeden zapis wystarcza dla
  // wszystkich tabel i przeżywa odtwarzanie p-table przez *ngIf): w trybie bez virtual
  // scroll zachowuje oryginalne zachowanie, a w trybie virtual scroll nie zapisuje
  // scrollLeft z powrotem do body (body jest masterem — inaczej powstaje pętla sprzężenia
  // z bodyScrollSyncListener i jitter przy przewijaniu w poziomie).
  private static patchScrollableViewHeaderScroll(): void {
    if (PrimeTableComponent.scrollableViewHeaderScrollPatched)
      return;
    PrimeTableComponent.scrollableViewHeaderScrollPatched = true;
    const proto = ScrollableView.prototype as any;
    if (typeof proto.onHeaderScroll !== 'function')
      return;
    proto.onHeaderScroll = function(this: any): void {
      const scrollLeft = this.scrollHeaderViewChild.nativeElement.scrollLeft;
      // Tryb bez virtual scroll: scrollBodyViewChild istnieje — oryginalne zachowanie PrimeNG
      // (nagłówek jest masterem, body/stopka podążają za nim).
      if (this.scrollBodyViewChild?.nativeElement) {
        this.scrollBodyViewChild.nativeElement.scrollLeft = scrollLeft;
        if (this.scrollFooterViewChild && this.scrollFooterViewChild.nativeElement)
          this.scrollFooterViewChild.nativeElement.scrollLeft = scrollLeft;
        this.preventBodyScrollPropagation = true;
      }
      // Tryb virtual scroll: scrollBodyViewChild nie istnieje. Body jest masterem i to ono
      // przewija nagłówek (bodyScrollSyncListener → syncHeaderToScrollLeft), więc NIE wolno zapisywać scrollLeft z powrotem
      // do body — asynchroniczny (opóźniony) event scroll nagłówka niósłby nieaktualną wartość
      // i cofał body w trakcie przewijania (jitter / "opieranie się" paskowi). Sam brak zapisu
      // eliminuje też pierwotny TypeError na undefined scrollBodyViewChild.
    };
  }

  // Klasy zaznaczenia wierszy liczone w szablonie z bieżących danych - patrz komentarz
  // w prime-table.component.html przy <tr>. Z dataKey isSelected to lookup O(1).
  isRowSelected(row: T): boolean {
    return this.table?.isSelected(row) === true;
  }

  isRowContextMenuHighlighted(row: T): boolean {
    return this.table?.contextMenuSelection != null && this.table.equals(row, this.table.contextMenuSelection);
  }

  private createDataSourceData(): void {
    this.dataSubject = new BehaviorSubject<Array<T | GroupedData<T>>>(null);
    this.dataSourceDataObj = this.dataSubject.asObservable();
    this.dataSubscription = this.matDataSource?.data$
      .pipe(takeUntil(this.unsubscribeChanges$))
      .subscribe(data => this.updateTableData(data));
    this.matDataSource?.loading$
      .pipe(
        filter(loading => loading === true),
        takeUntil(this.unsubscribeChanges$)
      )
      .subscribe(() => this.invalidateMenuOptionsCache());
  }

  private updateTableData(data: T[]): void {
    // Źródła bez znacznika wersji zbioru dają tylko bieżącą stronę, więc zestaw wartości filtra
    // traci ważność razem z nią. Tryb dzienny wystawia wersję i tam decyduje ona (patrz
    // dropDataFilterOptionsIfStale), a nie każde przewinięcie tabeli.
    if (!this.dataLoader?.getDataVersion) this.dataFilterOptionsCache.clear();
    this.zone.runOutsideAngular(() => {
      if (this.groupBy) {
        this.groupedData = groupData(data, this.groupBy, this.createSort(), this.translate);
        this.emitFlatData(this.buildFlatData(this.groupedData));
      } else {
        this.groupedData = null;
        this.emitFlatData(data);
      }
    });
    // rAF odpala się po tym jak Angular wyrenderuje nowe wiersze do DOM
    if (this.showMinimapValue && this.minimapDir) {
      this.minimapDir.minimapNeedsFullScan = true; // nowe dane → wymuś pełny rescan
      this.minimapDir.scheduleRender();
    }
    if (this.showRowRulerValue && this.rowRulerVisible && this.rowRulerDir) {
      // -1 wymusza rescan kolorów przy następnym renderze — łapie re-sort i re-filter
      // (długość może być taka sama, ale kolejność wierszy inna → stary cache jest błędny)
      this.rowRulerDir.invalidateDataLength();
      this.rowRulerDir.scheduleRender();
    }
    // CDK virtual scroll (OnPush) skips refresh when scroll position is unchanged.
    // checkViewportSize() schedules _doChangeDetection() → marks CDK viewport dirty →
    // triggers the second tick where ngDoCheck() processes the new _renderedItems.
    (this.table as any)?.scrollableViewChild?.virtualScrollBody?.checkViewportSize?.();
    // Strażnik na wyścigi przy pierwszym ładowaniu (paginator rysowany w połowie wysokości):
    // po każdym renderze danych dorównaj wysokość do dostępnego miejsca (no-op, gdy gap = 0).
    requestAnimationFrame(() => this.correctTableScrollHeight());
  }

  private buildFlatData(groups: GroupedData<T>[]): Array<GroupedData<T> | T> {
    const result: Array<GroupedData<T> | T> = [];
    for (const group of groups) {
      result.push(group);
      if (this.expandedGroups.has(group.groupName)) {
        for (const row of group.data) {
          result.push(row);
        }
      }
    }
    return result;
  }

  private rebuildFlatData(): void {
    if (this.groupedData) {
      this.zone.runOutsideAngular(() =>
        this.emitFlatData(this.buildFlatData(this.groupedData))
      );
    }
  }

  // Wspólne miejsce dla każdej zmiany renderowanej listy (pierwsze załadowanie, kolejne
  // paczki z serwera, zmiana dnia, ręczne zwinięcie/rozwinięcie grupy...) - niezależnie od
  // przyczyny, od razu odświeża limit skeletonu na podstawie świeżo policzonej długości
  // (a nie table._value, które po dataSubject.next() jeszcze przez chwilę jest nieaktualne -
  // async pipe zaktualizuje [value] dopiero na najbliższym cyklu CD, a robimy to poza zoną).
  // Bez tego limit zostawał ustawiony wg poprzedniej (dłuższej / "pełna wysokość") listy
  // aż do najbliższego eventu scroll - stąd skeleton bywał widoczny pod zwiniętymi grupami
  // np. od razu po wejściu w tabelę z domyślnie zwiniętym grupowaniem.
  private emitFlatData(items: Array<T | GroupedData<T>>): void {
    this.dataSubject.next(items);
    const rowCount = (items ?? []).filter(item => !this.isGroupHeader(item)).length;
    this.visibleRowCountSubject.next(rowCount);
    const w = this.getTableScrollWrapper();
    if (w) {
      this.updateSkeletonMaxHeight(w, (items ?? []).length);
      // Dane zmieniły się na "Brak danych" w trakcie trwania skeletonu (np. filtrowanie
      // do zera w środku 4-sekundowego okna po scrollu) - nie ma już czego chować, więc
      // gasimy go od razu, zamiast czekać na wygaśnięcie timera.
      if (rowCount === 0 && !this.skeletonLoadingActive) {
        if (this.skeletonHideTimer != null) {
          clearTimeout(this.skeletonHideTimer);
          this.skeletonHideTimer = null;
        }
        w.classList.remove('vs-skeleton-active');
      }
    }
    // Stan strzałek kompasu zależy od geometrii viewportu (scrollWidth/scrollHeight), która
    // zmienia się przy każdej nowej paczce danych (także przy przejściu do "Brak danych do
    // wyświetlenia") - bez tego strzałki odświeżały się tylko dzięki zdarzeniu scroll/resize,
    // których pusta, nieprzewijalna tabela nigdy sama nie wygeneruje.
    requestAnimationFrame(() => this.updateNavScrollState());
  }

  isGroupHeader(item: any): item is GroupedData<T> {
    return item instanceof GroupedData;
  }

  onGroupHeaderClick(group: GroupedData<T>): void {
    const wasExpanded = this.expandedGroups.has(group.groupName);
    if (wasExpanded) {
      this.expandedGroups.delete(group.groupName);
    } else {
      this.expandedGroups.add(group.groupName);
    }
    this.rebuildFlatData();
    this.resetBufferSize();
    if (!wasExpanded && group.data?.length) {
      this.rowFocus.emit(group.data[0]);
    }
  }

  refreshData(): void {
    this.updateDataSourceData();
    this.refreshSummary();
  }


  private loadData(): void {
    if (this.remoteDataType == RemoteDataType.WEBSOCKET)
      this.matDataSource.subscribeData(this.dataLoader.getDataType());
    else if (this.remoteDataType == RemoteDataType.REST) {
      this.updateDataSourceData();
      this.refreshSummary();
    }
  }

  onPage(event: { first: number; rows: number }): void {
    this.paginationSize = event.rows;
    this.updateDataSourceData();
    this.pageInput = (event.first / event.rows) + 1;
  }

  onSort(event: { multisortmeta: SortMeta[] }): void {
    if (this.table && this.matDataSource) {
      this.table.first = 0;
      this.resolveSort(event.multisortmeta);
      const sort = this.createSort();
      this.dataSource.sortBy = sort.map(s => SortBy.createFromTransformationsSort(s));
      this.sortReady = true;
      if (!this.selectableSort) this.toggleSort();
    }
  }

  toggleSort(): void {
    if (this.sortReady) {
      if (this.sortLocally) {
        this.matDataSource.sortDataLocally(this.createSort());
      } else {
        this.dataOptionsConnectorService.getActiveFilters()
          .subscribe(() => this.updateDataSourceData());
      }
      this.appliedSortMeta = [...this.multiSortMeta];
      this.sortReady = false;
    }
  }

  toggleSortMode(): void {
    this.selectableSort = !this.selectableSort;
    if (!this.selectableSort && this.sortReady) this.toggleSort();
  }

  private resolveSort(sortMeta: SortMeta[]): void {
    if (this.skipResolveSort) return;
    const currentMeta = new Map(this.multiSortMeta.map(meta => [meta.field, meta]));
    sortMeta?.forEach(newMeta => {
      const current = currentMeta.get(newMeta.field);
      if (!current || current.order !== -1)
        currentMeta.set(newMeta.field, newMeta);
      else currentMeta.delete(newMeta.field);
    });
    this.multiSortMeta = [...currentMeta.values()];
    this.table.multiSortMeta = this.multiSortMeta;
  }

  private createSort(): Sort[] | null {
    const sortArray: Sort[] = this.multiSortMeta.map(meta => ({
      active: meta.field,
      direction: meta.order == 1 ? SortDirection.ASC : SortDirection.DESC,
      valueOrder: this.findColumnSortValues(meta.field)
    })) || [];
    if (this.groupBy && !sortArray.map(m => m.active).includes(this.groupBy)) {
      sortArray.unshift({
        active: this.groupBy,
        direction: SortDirection.ASC,
        valueOrder: this.findColumnSortValues(this.groupBy)
      });
    }
    return sortArray;
  }

  // Sortowanie w pamięci widzi tylko nazwę pola i kierunek, więc własny porządek wartości kolumny
  // (ColumnDefinition.sortValues) trzeba dołożyć do żądania. Szukamy po initialFields, nie po widocznych
  // kolumnach - grupowanie może wskazywać kolumnę schowaną.
  private findColumnSortValues(field: string): string[] | undefined {
    return this.dataSource?.initialFields?.find(f => f.column.name === field)?.column.sortValues;
  }

  cancelLoading(): void {
    this.matDataSource?.cancel();
  }

  private updateDataSourceData(): void {
    this.getTransformations().subscribe(transformations => {
      if (transformations.filters?.criteria || this.autoloadData)
        this.matDataSource?.loadData(transformations);
    });
  }

  getTransformations(): Observable<Transformations> {
    const sort = this.createSort();

    let page: Page;
    if (this.table) {
      if (this.table.first !== null && this.table.first !== undefined && this.table.rows !== null && this.table.rows !== undefined) {
        const pageIndex = this.table.first / this.table.rows;
        page = {
          pageIndex,
          pageSize: this.table.rows
        };
      }
    }
    if (page == null) {
      page = {
        pageIndex: 0,
        pageSize: this.showPagination ? this.paginationSize : 100000
      };
    }

    return this.dataOptionsConnectorService.getActiveFilters()
      .pipe(map(filters => ({ sort, filters, page })));
  }

  // Własny resize kolumn — JEDNO źródło prawdy: nasz colgroup (getColgroupWidthStyle). Nie używamy
  // wbudowanego resize PrimeNG (columnResizeMode="expand"), bo ten imperatywnie nadpisywał szerokości
  // <col>/<table>/kontenerów przewijania równolegle do naszego colgroup — oba systemy rywalizowały, przez co
  // kolumny skakały przy hover (Angular przywracał wartość z modelu) albo przestawały reagować (błędny
  // offsetWidth po sekwencji przepełnienie→zmieszczenie). Podczas przeciągania NIE zmieniamy żadnych
  // szerokości — przesuwamy tylko pionową linię-wskaźnik (odpowiednik .p-column-resizer-helper z wbudowanego
  // resize PrimeNG), a po puszczeniu zapisujemy deltę do modelu i jedna detekcja zmian aktualizuje spójnie
  // nagłówek, dane i stopkę. Zmienia się szerokość WYŁĄCZNIE ciągniętej kolumny.
  private columnResizeState: {
    columnName: string;
    startX: number;
    startWidth: number;
    currentWidth: number;
    helperBaseLeft: number; // pozycja linii względem wrappera przy delcie 0 (prawa krawędź kolumny na starcie)
    helper: HTMLElement;
    move: (e: PointerEvent) => void;
    up: () => void;
  } | null = null;

  startColumnResize(event: PointerEvent, columnName: string): void {
    // Tylko lewy przycisk. Prawy obsługuje menu kontekstowe przypinania — to osobne zdarzenie
    // (contextmenu) zbindowane na p-table (onContextMenuSelected rozpoznaje uchwyt po .pt-column-resizer),
    // więc tu wystarczy nie zaczynać przeciągania.
    if (event.button !== 0)
      return;
    // preventDefault na pointerdown blokuje też pochodny mousedown, więc reorder (pReorderableColumn)
    // ani sortowanie nie startują; stopPropagation dla pewności (starsze silniki).
    event.preventDefault();
    event.stopPropagation();

    const th = (event.target as HTMLElement).closest('th');
    const wrapper = this.elementRef.nativeElement.querySelector('.table-ptable-wrapper') as HTMLElement | null;
    if (!th || !wrapper)
      return;

    const startWidth = this.getInitialColumnWidth(columnName);
    // Linia startuje na prawej krawędzi ciągniętej kolumny i przesuwa się o deltę szerokości
    // (z klamrą MIN_COLUMN_WIDTH — dalej w lewo nie zejdzie, tak jak finalna szerokość).
    const helperBaseLeft = th.getBoundingClientRect().right - wrapper.getBoundingClientRect().left;
    const helper = document.createElement('div');
    helper.className = 'pt-resize-helper';
    helper.style.left = helperBaseLeft + 'px';
    wrapper.appendChild(helper);

    const state = {
      columnName,
      startX: event.clientX,
      startWidth,
      currentWidth: startWidth,
      helperBaseLeft,
      helper,
      move: (e: PointerEvent) => this.onColumnResizeMove(e),
      up: () => this.onColumnResizeEnd()
    };
    this.columnResizeState = state;

    // Capture na uchwycie: pointermove/pointerup docierają nawet po wyjechaniu kursora poza okno
    // przeglądarki (bez capture puszczenie przycisku poza oknem gubiło mouseup i drag się "przyklejał").
    (event.target as HTMLElement).setPointerCapture?.(event.pointerId);

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    // Poza strefą Angulara — pointermove nie odpala detekcji zmian; przesuwamy tylko linię-wskaźnik w DOM.
    this.zone.runOutsideAngular(() => {
      document.addEventListener('pointermove', state.move);
      document.addEventListener('pointerup', state.up);
      document.addEventListener('pointercancel', state.up);
    });
  }

  private onColumnResizeMove(event: PointerEvent): void {
    const state = this.columnResizeState;
    if (!state)
      return;
    const newWidth = Math.max(PrimeTableComponent.MIN_COLUMN_WIDTH, state.startWidth + (event.clientX - state.startX));
    state.currentWidth = newWidth;
    state.helper.style.left = (state.helperBaseLeft + (newWidth - state.startWidth)) + 'px';
  }

  private onColumnResizeEnd(): void {
    const state = this.columnResizeState;
    this.columnResizeState = null;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    if (!state)
      return;
    document.removeEventListener('pointermove', state.move);
    document.removeEventListener('pointerup', state.up);
    document.removeEventListener('pointercancel', state.up);
    state.helper.remove();

    // Zablokuj pochodny click na nagłówku (inaczej pSortableColumn posortowałby po tej kolumnie).
    this.suppressNextHeaderClick();

    const delta = Math.round(state.currentWidth - state.startWidth);
    if (!delta)
      return;
    // Zapis do modelu; detekcja zmian odtwarza colgroup (getColgroupWidthStyle) i --pt-table-width,
    // więc nagłówek, dane i stopka zmieniają szerokość jednocześnie.
    this.zone.run(() => {
      this.tableColumnsService.updateDataTableColumnSize(this.dataSource.name, state.columnName, delta);
      this.columnSettingsVersion++;
      this.changeDetector.markForCheck();
    });
    // Nowe szerokości kolumn → minimapa musi przerysować się z aktualnym układem.
    this.requestMinimapRescan();
  }

  columnReorder(event: any): void {
    if (event.columns)
      this.tableColumnsService.updateDataTableColumnsPositions(this.dataSource.name, event.columns);
    // PrimeNG przestawia kolumny w swojej tablicy in-place (ta sama referencja displayedColumns), więc
    // nagłówek się przerysowuje, ale wiersze danych (app-content-row-columns, OnPush, [columns]=displayedColumns)
    // nie dostają nowego inputu i zostają w starej kolejności. Odbudowujemy displayedColumns jako NOWĄ
    // referencję z właśnie zapisanych pozycji — nagłówek, dane i minimapa (rescan w setColumns) przechodzą
    // na nową kolejność spójnie.
    this.setColumns(this.getCurrentColumnsSettings());
  }

  // Wymusza pełny rescan offscreen-canvasu minimapy po tym, jak Angular zastosuje zmiany układu kolumn
  // w DOM (nowa kolejność przy reorder, nowe szerokości przy resize).
  // MutationObserver minimapy sam z siebie nie łapie tych przypadków: zmiany szerokości <col> to atrybut
  // `style` (obserwujemy tylko `class`), a reorder to childList, który wywołuje jedynie przemalowanie ze
  // starego cache. rAF odkłada rescan o klatkę, żeby czytać już przebudowany DOM.
  private requestMinimapRescan(): void {
    if (!this.showMinimapValue || !this.minimapDir)
      return;
    this.zone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        if (!this.minimapDir)
          return;
        this.minimapDir.minimapNeedsFullScan = true;
        this.minimapDir.scheduleRender();
      });
    });
  }

  // Po przeciągnięciu uchwytu resize pointerup wypada zwykle poza samym uchwytem, więc przeglądarka
  // generuje `click` na <th>, a pSortableColumn odczytuje go jako żądanie sortowania. Zjadamy ten jeden
  // click w fazie przechwytywania, zanim dotrze do host-handlera sortowania. setTimeout sprząta listener,
  // gdy click nie padnie (np. puszczenie poza dokumentem).
  private suppressNextHeaderClick(): void {
    const swallow = (e: Event): void => {
      e.stopPropagation();
      e.preventDefault();
      cleanup();
    };
    const cleanup = (): void => {
      document.removeEventListener('click', swallow, true);
      clearTimeout(timer);
    };
    const timer = setTimeout(cleanup, 0);
    document.addEventListener('click', swallow, true);
  }

  // Komórki liczą się w ngOnChanges wiersza (OnPush), więc zmiana stanu spoza tabeli wymaga podania
  // kolumn jako nowej referencji.
  refreshCells(): void {
    if (this.displayedColumns?.length)
      this.displayedColumns = [...this.displayedColumns];
  }

  // Przemalowanie wierszy (tableRowFormatter) po zmianie reguły spoza tabeli. Osobno od refreshCells:
  // tam chodzi o treść komórek, tu tylko o formatowanie wiersza.
  refreshRowStyles(): void {
    this.changeDetector.markForCheck();
    // Linijka wierszy trzyma kolory w cache'u budowanym raz na porcję danych, a tu dane nie drgnęły -
    // bez unieważnienia pasek zostałby w starych barwach.
    if (this.showRowRulerValue && this.rowRulerVisible && this.rowRulerDir) {
      this.rowRulerDir.invalidateDataLength();
      this.rowRulerDir.scheduleRender();
    }
  }

  private setColumns(columnsSettings: ColumnSetting[]): void {
    const initialFields = cloneDeep(this.dataSource.fields.filter(v => v.valueField.visible).map(v => v.column));
    const settings = new Map(columnsSettings.map(item => [item.name, item]));
    // Kolumny przypięte (column.locked) nie mają wpisu w ustawieniach - idą zawsze na początek, zaraz
    // za "options", niezależnie od zapisanych pozycji i widoczności.
    const lockedColumns = initialFields.filter(c => c.locked);
    let columns = initialFields.filter(c => !c.locked && settings.get(c.name)?.visible);
    columns.sort((a, b) => {
      const posA = settings.get(a.name).position ?? 0;
      const posB = settings.get(b.name).position ?? 0;
      return posA - posB;
    });
    // Doklejane przed obsługą nagłówków grupujących: kolumna przypięta nie ma groupingHeaderName, więc
    // trafia do grupy "no-grouping" i wiersz nagłówków grup zgadza się liczbą komórek.
    columns = [...lockedColumns, ...columns];

    if (columns.length > 0) {
      if (this.groupingHeaderEnabled) {
        this.createGroupingHeaders(columns);
        columns = this.sortColumnsByGroupingHeaders(columns);
      }
      this.displayedColumns = [this.optionsColumn, ...columns];
    }
    else
      this.displayedColumns = [];
    // Zmiana zestawu/kolejności kolumn (widoczność, grupowanie, reset) → minimapa musi przerysować się
    // z nowym układem po tym, jak Angular wyrenderuje kolumny.
    this.requestMinimapRescan();
  }

  // Bez kolumn przypiętych: z tych ustawień powstaje lista widoczności/kolejności i zapis szerokości, a
  // przypiętej nie można ani ukryć, ani przestawić, ani zwężyć.
  private getInitialColumnsSettings(): ColumnSetting[] {
    return this.dataSource.initialFields
      .filter(v => v.valueField.visible && !v.valueField.permanentlyHidden && !v.column.locked)
      .map((field, idx) => ({
        name: field.column.name,
        visible: field.column.visible,
        position: idx,
        width: field.column.defaultWidth
      } as ColumnSetting));
  }

  private getCurrentColumnsSettings(): ColumnSetting[] {
    const settings = this.tableColumnsService.getDataTableColumnsSettings(this.dataSource.name);
    return this.getInitialColumnsSettings().map(s => {
      const cached = settings[s.name];
      if (cached != null) {
        s.position = cached.position ?? s.position;
        s.width = cached.width ?? s.width ?? PrimeTableComponent.DEFAULT_COLUMN_WIDTH;
        s.visible = cached.visible ?? s.visible;
      }
      return s;
    });
  }

  trackByGroupingColumnName = (index: number, col: { name: string; label: string }): string => col.name;

  get filteredGroupingColumns(): { name: string; label: string }[] {
    if (!this.dataSource) return [];
    const term = this.groupingSearchTerm.toLowerCase().trim();
    const visibleColumnNames = new Set(this.getCurrentColumnsSettings().filter(s => s.visible).map(s => s.name));
    const columns = this.dataSource.initialFields
      .filter(f => f.valueField.canGroup && !f.valueField.primaryKey && f.column.name !== 'rowId' && visibleColumnNames.has(f.column.name))
      .map(f => {
        const raw = this.translate.instant(f.column.title);
        return { name: f.column.name, label: raw.charAt(0).toUpperCase() + raw.slice(1) };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
    return term ? columns.filter(c => c.label.toLowerCase().includes(term)) : columns;
  }

  openGroupingMenu(): void {
    this.groupingPanel.toggle();
  }

  onGroupingOverlayShow(): void {
    setTimeout(() => this.groupingPanel?.panelElement
      ?.querySelector<HTMLElement>('.grouping-search-input')?.focus());
  }

  groupingKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      if (this.groupingSearchTerm) {
        this.groupingSearchTerm = '';
        this.groupingSelectedIndex = -1;
      } else {
        this.groupingPanel.close();
      }
      return;
    }
    const cols = this.filteredGroupingColumns;
    if (!cols.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.groupingSelectedIndex = Math.min(this.groupingSelectedIndex + 1, cols.length - 1);
      this.scrollGroupingActiveIntoView();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.groupingSelectedIndex = Math.max(this.groupingSelectedIndex - 1, 0);
      this.scrollGroupingActiveIntoView();
    } else if (event.key === 'Enter' && this.groupingSelectedIndex >= 0) {
      this.onSelectGroupingColumn(cols[this.groupingSelectedIndex].name);
      this.groupingPanel.close();
    }
  }

  private scrollGroupingActiveIntoView(): void {
    setTimeout(() => {
      const el = document.querySelector('.grouping-item-highlighted');
      if (el) { el.scrollIntoView({ block: 'nearest' }); }
    });
  }

  disableGroupingOnRightClick(event: MouseEvent): void {
    event.preventDefault();
    if (this.groupBy) this.onSelectGroupingColumn(null);
  }

  get areAnyGroupsExpanded(): boolean {
    return this.groupBy ? this.expandedGroups.size > 0 : false;
  }

  get areAllGroupsExpanded(): boolean {
    if (!this.groupBy || !this.groupedData?.length) return false;
    return this.groupedData.every(g => this.expandedGroups.has(g.groupName));
  }

  toggleAllGroups(): void {
    if (!this.groupedData) return;
    if (this.areAnyGroupsExpanded) {
      this.expandedGroups.clear();
    } else {
      this.groupedData.forEach(g => this.expandedGroups.add(g.groupName));
    }
    this.rebuildFlatData();
    this.resetBufferSize();
  }

  openColumnVisibilityOverlay(): void {
    if (this.columnVisibilityPanel.isOpen) {
      this.columnVisibilityPanel.close();
      return;
    }
    this.columnVisibilitySettings = this.getCurrentColumnsSettings().map(v =>
      new ColumnVisabilityPositionSetting(
        v.name, v.visible, v.position, v.width,
        this.dataSource.fields.find(f => f.column.name === v.name)?.column?.title
      )
    );
    this.pendingColumnVisibilitySettings = [...this.columnVisibilitySettings];
    this.pendingFrozenColumnsCount = this.frozenColumnsCount;
    this.columnVisibilityPanel.open();
  }

  onColumnVisibilityChanged(event: { value: ColumnVisabilityPositionSetting[]; isValid: boolean }): void {
    this.pendingColumnVisibilitySettings = event.value;
    this.columnVisibilityFormValid = event.isValid;
  }

  saveColumnVisibility(): void {
    this.onHideColumns(this.pendingColumnVisibilitySettings);
    this.setFrozenColumnsCount(this.pendingFrozenColumnsCount);
    this.columnVisibilityPanel?.close();
  }

  onColumnListDragChange(dragging: boolean): void {
    if (dragging) {
      this.isColumnListDragging = true;
    } else {
      setTimeout(() => this.isColumnListDragging = false, 0);
    }
  }

  onColumnVisibilityClosed(): void {
    this.rebuildFlatData();
    this.resetBufferSize();
  }

  openTableSettings(): void {
    const columnOptionsDialogData = {
      columsSetId: this.tableColumnsService.getColumnsSetId(this.dataSource.name),
      columnsSettings: this.getCurrentColumnsSettings().map(v => new ColumnVisabilityPositionSetting(
        v.name,
        v.visible,
        v.position,
        v.width,
        this.dataSource.fields.find(f => f.column.name === v.name)?.column?.title
      )
      ),
      possibleToGrouping: this.dataSource.initialFields
        .filter(f => f.valueField.canGroup && !f.valueField.primaryKey && f.column.name !== 'rowId')
        .map(f => ({ name: f.column.name, title: f.column.title })),
      groupingEnabled: this.groupingEnabled,
      selectedGroupingColumn: this.groupBy,
      groupingColumns: this.dataSource.fields
        .filter(v => v.valueField.canGroup && !v.valueField.primaryKey && v.column.name !== 'rowId')
        .map(v => v.column.name),
      summaryEnabled: this.summaryEnabled,
      displayFooter: this.displayFooter,
      summaryType: this.summaryType,
      datasource: this.dataSource.name,
      frozenColumnsCount: this.frozenColumnsCount,
      lockedColumnsCount: this.lockedColumnsCount
    };
    this.matDialog.open(ColumnSettingsDialogComponent, {
      data: columnOptionsDialogData,
      autoFocus: false,
      restoreFocus: false
    })
      .afterClosed()
      .pipe(filter(result => result))
      .subscribe((result: ColumnSettingsDialogData) => this.colSettingsChange(result));
  }

  colSettingsChange = (result: ColumnSettingsDialogData | null): void => {
    if (result) {
      const actions: Map<ColumnSettingsActionName, any> = result.actions;
      actions.forEach((value: any, key: ColumnSettingsActionName) => {
        if (key == ColumnSettingsActionName.HIDE_COLUMNS)
          this.onHideColumns(value);
        else if (key == ColumnSettingsActionName.SELECT_GROUPING_COLUMN)
          this.onSelectGroupingColumn(value);
        else if (key == ColumnSettingsActionName.RESET_SETTINGS)
          this.onResetSettings();
        else if (key == ColumnSettingsActionName.DISABLE_SUMMARY) {
          this.summaryType = null;
          this.displayFooter = value;
          this.refreshSummary();
        }
        else if (key == ColumnSettingsActionName.ENABLE_AND_SELECT_SUMMARY_TYPE) {
          this.summaryType = value.type;
          this.displayFooter = value.displayFooter;
          this.refreshSummary();
        } else if (key == ColumnSettingsActionName.SET_OF_COLUMNS_SELECTED) {
          if (value != null)
            this.tableColumnsService.setColumnsSetId(this.dataSource.name, value);
          else
            this.tableColumnsService.resetColumnsSetId(this.dataSource.name);
        } else if (key == ColumnSettingsActionName.FREEZE_COLUMNS)
          this.setFrozenColumnsCount(value);
      });
    }
  };

  resetSort(): void {
    if (this.multiSortMeta?.length) {
      this.table.multiSortMeta = this.multiSortMeta = [];
      this.table.sortMultiple();
      if (!this.selectableSort) this.toggleSort();
    }
  }

  setSort(sortBy: SortBy[]): void {
    this.table.multiSortMeta = this.multiSortMeta = sortBy.map(s => s.toMultiSortMeta());
    this.skipResolveSort = true;
    this.table.sortMultiple();
    this.skipResolveSort = false;
  }

  openSortOverlay(): void {
    if (this.sortPanel.isOpen) {
      this.sortPanel.close();
      return;
    }
    const pendingFields = new Set(this.multiSortMeta.map(m => m.field));
    const appliedNotInPending = this.appliedSortMeta.filter(m => !pendingFields.has(m.field));
    const appliedIndexMap = new Map(this.appliedSortMeta.map((m, i) => [m.field, i]));
    const appliedOrderMap = new Map(this.appliedSortMeta.map(m => [m.field, m.order as 1 | -1]));
    this.sortOverlayItems = [
      ...this.multiSortMeta.map(m => ({
        field: m.field, order: m.order as 1 | -1, active: true,
        appliedOrder: appliedOrderMap.get(m.field) ?? null,
        appliedIndex: appliedIndexMap.has(m.field) ? appliedIndexMap.get(m.field) : null
      })),
      ...appliedNotInPending.map(m => ({
        field: m.field, order: m.order as 1 | -1, active: false,
        appliedOrder: m.order as 1 | -1,
        appliedIndex: appliedIndexMap.get(m.field) ?? null
      }))
    ];
    this.sortPanel.open();
  }

  onSortIconRightClick(event: MouseEvent): void {
    event.preventDefault();
    this.resetSort();
  }

  get sortOverlayHasChanges(): boolean {
    const active = this.sortOverlayItems.filter(i => i.active);
    if (active.length !== this.appliedSortMeta.length) return true;
    return active.some((item, idx) =>
      item.field !== this.appliedSortMeta[idx]?.field ||
      item.order !== this.appliedSortMeta[idx]?.order
    );
  }

  resetSortOverlayToApplied(): void {
    this.sortOverlayItems = this.appliedSortMeta.map((m, i) => ({
      field: m.field, order: m.order as 1 | -1, active: true,
      appliedOrder: m.order as 1 | -1, appliedIndex: i
    }));
  }

  onConfirmSortRightClick(event: MouseEvent): void {
    event.preventDefault();
    if (!this.sortReady) return;
    this.table.multiSortMeta = this.multiSortMeta = this.appliedSortMeta.map(m => ({ ...m }));
    this.skipResolveSort = true;
    this.table.sortMultiple();
    this.skipResolveSort = false;
    this.sortReady = false;
  }

  closeSortOverlay(): void {
    this.sortPanel?.close();
  }

  saveSortFromOverlay(): void {
    const newSort: SortMeta[] = this.sortOverlayItems
      .filter(i => i.active)
      .map(i => ({ field: i.field, order: i.order }));
    this.table.multiSortMeta = this.multiSortMeta = newSort;
    this.skipResolveSort = true;
    this.table.sortMultiple();
    this.skipResolveSort = false;
    this.sortPanel?.close();
  }

  pendingSortDrop(event: { previousIndex: number; currentIndex: number }): void {
    moveItemInArray(this.sortOverlayItems, event.previousIndex, event.currentIndex);
    this.sortOverlayItems = [...this.sortOverlayItems];
  }

  toggleSortOverlayItem(index: number): void {
    const item = this.sortOverlayItems[index];
    if (item?.active && item.appliedOrder === null) {
      this.sortOverlayItems = this.sortOverlayItems.filter((_, i) => i !== index);
    } else {
      const items = [...this.sortOverlayItems];
      items[index] = { ...items[index], active: !items[index].active };
      this.sortOverlayItems = items;
    }
  }

  pendingSortSetDir(index: number, order: 1 | -1): void {
    const items = [...this.sortOverlayItems];
    items[index] = { ...items[index], order };
    this.sortOverlayItems = items;
  }

  get sortOverlayAllActive(): boolean {
    return this.sortOverlayItems.length > 0 && this.sortOverlayItems.every(i => i.active);
  }

  toggleAllSortOverlayItems(): void {
    const allActive = this.sortOverlayAllActive;
    if (allActive) {
      this.sortOverlayItems = this.sortOverlayItems
        .filter(i => i.appliedOrder !== null)
        .map(i => ({ ...i, active: false }));
    } else {
      this.sortOverlayItems = this.sortOverlayItems.map(i => ({ ...i, active: true }));
    }
  }

  pendingSortAddKey(field: string): void {
    this.sortOverlayItems = [...this.sortOverlayItems, { field, order: 1, active: true, appliedOrder: null, appliedIndex: null }];
  }

  sortAddKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      if (this.sortAddSearch) {
        this.sortAddSearch = '';
        this.sortAddSelectedIndex = -1;
      } else {
        this.closeSortOverlay();
      }
      return;
    }
    const cols = this.filteredColumnsToAdd;
    if (!cols.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.sortAddSelectedIndex = Math.min(this.sortAddSelectedIndex + 1, cols.length - 1);
      this.scrollSortAddActiveIntoView();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.sortAddSelectedIndex = Math.max(this.sortAddSelectedIndex - 1, 0);
      this.scrollSortAddActiveIntoView();
    } else if (event.key === 'Enter' && this.sortAddSelectedIndex >= 0) {
      this.pendingSortAddKey(cols[this.sortAddSelectedIndex].name);
      this.sortAddSelectedIndex = -1;
    }
  }

  private scrollSortAddActiveIntoView(): void {
    setTimeout(() => {
      const el = document.querySelector('.sort-add-result-item-active');
      if (el) { el.scrollIntoView({ block: 'nearest' }); }
    });
  }

  getSortKeyNumber(index: number): number | null {
    if (!this.sortOverlayItems[index]?.active) return null;
    let count = 0;
    for (let i = 0; i <= index; i++) {
      if (this.sortOverlayItems[i]?.active) count++;
    }
    return count;
  }

  isSortKeyNew(index: number): boolean {
    const item = this.sortOverlayItems[index];
    return !!item?.active && item.appliedOrder === null;
  }

  isSortKeyPositionChanged(index: number): boolean {
    const item = this.sortOverlayItems[index];
    if (!item?.active) return false;
    if (item.appliedOrder === null) return false;
    if (item.appliedIndex === null) return true;
    const currentPos = this.getSortKeyNumber(index);
    return currentPos !== null && item.appliedIndex !== (currentPos - 1);
  }

  isSortKeyDirectionChanged(index: number): boolean {
    const item = this.sortOverlayItems[index];
    return !!item?.active && item.appliedOrder !== null && item.order !== item.appliedOrder;
  }

  onSortListDragEnd(): void {
    setTimeout(() => this.isSortListDragging = false, 0);
  }

  getColumnTitle(field: string): string {
    return this.displayedColumns.find(c => c.name === field)?.title ?? field;
  }

  openFilterOverlay(): void {
    if (this.filterPanel.isOpen) {
      this.filterPanel.close();
      return;
    }
    this.filterOverlayItems = this.buildFilterOverlayItems();
    this.filterPanel.open();
  }

  closeFilterOverlay(): void {
    this.filterPanel?.close();
  }

  resetFilterOverlayToApplied(): void {
    this.filterOverlayItems = this.buildFilterOverlayItems();
  }

  saveFiltersFromOverlay(): void {
    this.commitFilterOverlay();
    this.filterPanel?.close();
  }

  applyFiltersFromOverlay(): void {
    this.commitFilterOverlay();
    this.filterOverlayItems = this.buildFilterOverlayItems();
  }

  private commitFilterOverlay(): void {
    const entries = this.filterOverlayItems.map(i => ({ name: i.field, value: i.active ? i.value : null }));
    this.columnFilterService.applyValues(entries);
  }

  onFilterValueEnter(event: KeyboardEvent): void {
    event.preventDefault();
    this.filterAddSearch = '';
    this.filterAddSelectedIndex = -1;
    setTimeout(() => this.filterPanel?.panelElement
      ?.querySelector<HTMLElement>('.filter-add-input')?.focus());
  }

  toggleFilterOverlayItem(index: number): void {
    const item = this.filterOverlayItems[index];
    if (item?.active && item.appliedValue === null) {
      this.filterOverlayItems = this.filterOverlayItems.filter((_, i) => i !== index);
    } else {
      const items = [...this.filterOverlayItems];
      items[index] = { ...items[index], active: !items[index].active };
      this.filterOverlayItems = items;
    }
  }

  toggleAllFilterOverlayItems(): void {
    if (this.filterOverlayAllActive) {
      this.filterOverlayItems = this.filterOverlayItems
        .filter(i => i.appliedValue !== null)
        .map(i => ({ ...i, active: false }));
    } else {
      this.filterOverlayItems = this.filterOverlayItems.map(i => ({ ...i, active: true }));
    }
  }

  pendingFilterAddKey(field: string): void {
    if (this.isFilterTypeUnsupported(this.findFieldByName(field)?.type)) return;
    this.filterOverlayItems = this.sortFilterItemsByTitle(
      [...this.filterOverlayItems, { field, value: null, active: true, appliedValue: null }]
    );
    this.focusFilterValueInput(field);
  }

  isColumnFilterUnsupported(col: ColumnDefinition): boolean {
    const field = this.findFieldByName(col.name);
    return field?.canFilter === false || this.isFilterTypeUnsupported(field?.type);
  }

  // No FieldType is excluded right now, but this stays as the single place to add one -
  // some types may turn out to need it (e.g. ones with no meaningful rendered text to match).
  private static readonly FILTER_UNSUPPORTED_TYPES: FieldType[] = [];

  private isFilterTypeUnsupported(type: FieldType | undefined): boolean {
    return PrimeTableComponent.FILTER_UNSUPPORTED_TYPES.includes(type);
  }

  isFilterMembershipChanged(index: number): boolean {
    const item = this.filterOverlayItems[index];
    if (!item) return false;
    if (item.appliedValue === null) return item.active;
    return !item.active;
  }

  isFilterValueChanged(index: number): boolean {
    const item = this.filterOverlayItems[index];
    if (!item?.active || item.appliedValue === null) return false;
    return !isEqual(item.value, item.appliedValue);
  }

  onFilterIconRightClick(event: MouseEvent): void {
    event.preventDefault();
    this.columnFilterService.clearAllColumnFilters();
    this.filterOverlayItems = [];
  }

  private focusFilterValueInput(field: string): void {
    setTimeout(() => {
      const container = document.querySelector(`.filter-value-control[data-filter-field="${CSS.escape(field)}"]`);
      const focusable = container?.querySelector<HTMLElement>('input, [tabindex]');
      if (!focusable) return;
      focusable.focus();
      if (focusable instanceof HTMLInputElement && (focusable.type === 'text' || focusable.type === 'search')) {
        focusable.select();
      }
    });
  }

  filterAddKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      if (this.filterAddSearch) {
        this.filterAddSearch = '';
        this.filterAddSelectedIndex = -1;
      } else {
        this.closeFilterOverlay();
      }
      return;
    }
    const cols = this.filteredColumnsToAddFilter;
    if (!cols.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.filterAddSelectedIndex = Math.min(this.filterAddSelectedIndex + 1, cols.length - 1);
      this.scrollFilterAddActiveIntoView();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.filterAddSelectedIndex = Math.max(this.filterAddSelectedIndex - 1, 0);
      this.scrollFilterAddActiveIntoView();
    } else if (event.key === 'Enter' && this.filterAddSelectedIndex >= 0) {
      this.pendingFilterAddKey(cols[this.filterAddSelectedIndex].name);
      this.filterAddSelectedIndex = -1;
    }
  }

  private scrollFilterAddActiveIntoView(): void {
    setTimeout(() => {
      const el = document.querySelector('.filter-add-result-item-active');
      if (el) { el.scrollIntoView({ block: 'nearest' }); }
    });
  }

  private buildFilterOverlayItems(): FilterOverlayItem[] {
    return this.filterableColumns
      .filter(c => !!this.columnFilterService.getSavedValue(c.name))
      .sort((a, b) => this.compareColumnTitles(a, b))
      .map(c => {
        const value = this.columnFilterService.getSavedValue(c.name);
        return { field: c.name, value, active: true, appliedValue: value };
      });
  }

  getFilterFieldWidget(field: string): 'boolean' | 'value-list' | 'default' {
    const valueField = this.findFieldByName(field);
    if (valueField?.type === FieldType.BOOLEAN) return 'boolean';
    if (valueField?.options?.length > 0 || valueField?.filterOptionsFromData) return 'value-list';
    return 'default';
  }

  getFilterFieldOptions(field: string): Option[] | undefined {
    return this.findFieldByName(field)?.options;
  }

  getFilterFieldEmptyOptionLabel(field: string): string | null {
    return this.findFieldByName(field)?.filterEmptyOptionLabel ?? null;
  }

  /**
   * Lista unikalnych wartości kolumny do filtra kolumnowego (ValueField.filterOptionsFromData).
   * Liczona z całości danych wczytanych do tabeli - w trybie dziennym Odjazdów to cała doba, a nie
   * tylko widoczna strona - i tylko na żądanie, przy otwarciu listy; wynik trzymamy do najbliższej
   * zmiany danych, żeby kolejne otwarcia nie przechodziły po wierszach od nowa.
   */
  private getDataFilterOptions(columnName: string): Option[] {
    this.dropDataFilterOptionsIfStale();
    const cached = this.dataFilterOptionsCache.get(columnName);
    if (cached) return cached;
    const options = this.collectDataFilterOptions(columnName);
    this.dataFilterOptionsCache.set(columnName, options);
    return options;
  }

  /**
   * Źródła trzymające całość danych w pamięci (tryb dzienny) wystawiają znacznik wersji zbioru -
   * wtedy raz policzone wartości żyją do zmiany doby albo dojścia kolejnej paczki, zamiast być
   * liczone od nowa przy każdym przewinięciu tabeli. Reszta źródeł zna tylko bieżącą stronę, więc
   * dla nich zestaw kasuje się razem z danymi (patrz updateTableData).
   */
  private dropDataFilterOptionsIfStale(): void {
    const version = this.dataLoader?.getDataVersion?.() ?? null;
    if (version === null || version === this.dataFilterOptionsVersion) return;
    this.dataFilterOptionsCache.clear();
    this.dataFilterOptionsVersion = version;
  }

  /**
   * Na liście stoją wartości surowe - dla kolumn, których komórka pokazuje wartość ozdobioną, bierze
   * się je z ValueField.rawValueFieldName (nr boczny pojazdu: "5301", a nie "5301✍️" czy
   * "5301 ⬅️ 5299"). Po tym samym polu idzie potem dopasowanie wyboru z listy, patrz
   * ColumnFilterService.
   */
  private collectDataFilterOptions(columnName: string): Option[] {
    const field = this.findFieldByName(columnName);
    if (!field) return [];
    const valueFieldName = field.rawValueFieldName ?? columnName;
    const values = new Set<string>();
    for (const row of this.getAllLoadedRows()) {
      const raw = row[valueFieldName];
      // Kolumny wielowartościowe (np. uchybienia odjazdu) trzymają w wierszu tablicę - na liście
      // ma się znaleźć każda z wartości osobno.
      for (const value of (Array.isArray(raw) ? raw : [raw])) {
        if (value === null || value === undefined || value === '') continue;
        values.add(String(value));
      }
    }
    return [...values]
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
      .map(value => ({ key: value, value }));
  }

  private getAllLoadedRows(): T[] {
    const allLoaded = this.dataLoader?.getAllLoadedData?.(null);
    if (allLoaded?.length) return allLoaded;
    // Wiersze wystawione do tabeli są już po filtrach kolumnowych - lista zbudowana z nich
    // zawężałaby się do tego, co wybrane.
    const allData = this.matDataSource?.getAllData();
    if (allData?.length) return allData;
    return (this.dataSubject?.value ?? []).filter(item => !this.isGroupHeader(item)) as T[];
  }

  getFilterNativeInputType(field: string): string {
    return this.findFieldByName(field)?.type === FieldType.TIME ? 'time' : 'search';
  }

  private sortFilterItemsByTitle(items: FilterOverlayItem[]): FilterOverlayItem[] {
    const columnByName = new Map(this.filterableColumns.map(c => [c.name, c]));
    return [...items].sort((a, b) => this.compareColumnTitles(columnByName.get(a.field), columnByName.get(b.field)));
  }

  rowSelected(data: T): void {
    // Kliknięty wiersz staje się kotwicą ciągu budowanego shiftem (patrz rowKey).
    this.rangeAnchorKey = this.getRowPk(data);
    this.rowClicked.emit(data);
  }

  private printMenuOptions(): MenuItem {
    return {
      label: this.translate.instant('local-print.print-to-file'),
      icon: 'pi pi-print',
      items: [
        {
          label: this.translate.instant('PDF'),
          icon: 'pi pi-file-pdf',
          disabled: this.printService.isPdfBusy,
          command: () => this.print('pdf', Array.isArray(this.selectedElement) ? this.selectedElement : [this.selectedElement])
        },
        {
          label: this.translate.instant('XLSX'),
          icon: 'pi pi-file-excel',
          disabled: this.printService.isExcelBusy,
          command: () => this.print('xlsx', Array.isArray(this.selectedElement) ? this.selectedElement : [this.selectedElement])
        }
      ]
    };
  }

  get shouldShowContextMenu(): boolean {
    return this.buildMenuOptions != null ||
      (this.printService.enabled && this.displayedColumns?.length && !this.groupBy && !this.expandableDeviceStatusRow && !this.expandableSnvrTask);
  }

  private hasMenuOptionsCache = new Map<any, boolean>();
  private menuOptionsCache = new Map<any, MenuItem[]>();

  private menuOptionsCacheKey(data: T | T[]): any {
    if (Array.isArray(data))
      return data.map(d => d?.[this.dataPrimaryKey]).join(',');
    return data?.[this.dataPrimaryKey] ?? data;
  }

  private invalidateMenuOptionsCache(data?: T | T[]): void {
    this.menuOptionsCache.clear();
    if (!this.hasMenuOptionsCacheEnabled)
      return;
    if (data == null) {
      this.hasMenuOptionsCache.clear();
    } else {
      this.hasMenuOptionsCache.delete(this.menuOptionsCacheKey(data));
    }
  }

  hasMenuOptionsForRow(data: T | T[]): boolean {
    if (this.buildMenuOptions == null)
      return false;
    if (this.hasMenuOptionsCacheEnabled) {
      const key = this.menuOptionsCacheKey(data);
      if (key != null && this.hasMenuOptionsCache.has(key))
        return this.hasMenuOptionsCache.get(key);
      const result = this.buildMenuOptions(data, this.userProfileService.getUserProfile())
        ?.some(x => x.visible === true) ?? false;
      if (key != null)
        this.hasMenuOptionsCache.set(key, result);
      return result;
    }
    return this.buildMenuOptions(data, this.userProfileService.getUserProfile())
      ?.some(x => x.visible === true) ?? false;
  }

  getGroupAwareRowIndex(row: T, rowIndex: number): number {
    if (!this.groupBy || !this.groupedData) return rowIndex;
    for (const group of this.groupedData) {
      const idx = group.data.indexOf(row);
      if (idx !== -1) return idx;
    }
    return rowIndex;
  }

  private createMenuOptions(data: T | T[]): MenuItem[] {
    const key = this.menuOptionsCacheKey(data);
    if (key != null && this.menuOptionsCache.has(key))
      return this.menuOptionsCache.get(key);
    let result: MenuItem[];
    const printMenu = this.selectionType === 'multiple' ? [this.printMenuOptions()] : [];
    if (this.buildMenuOptions == null) {
      result = printMenu;
    } else {
      const menuOptions = this.buildMenuOptions(data, this.userProfileService.getUserProfile());
      // Kreska stoi w liście bezwarunkowo - menu odsiewa separatory bez sąsiada
      // (patrz ContextSubmenuComponent.visible).
      const mergedMenu: MenuItem[] = [...(menuOptions ?? []), { separator: true }, ...printMenu];
      result = mergedMenu.some(x => x.visible === true) ? mergedMenu : [];
    }
    if (key != null) this.menuOptionsCache.set(key, result);
    return result;
  }

  private shouldShowSelectOptions(data: T | T[]): boolean {
    const menuOptions = this.createMenuOptions(data);
    if (menuOptions.length > 0) {
      this.contentMenuOptions = menuOptions;
      return true;
    } else {
      this.contentMenuOptions = [];
      return false;
    }
  }

  onRowRightClick(event: MouseEvent, row: T): void {
    this.contextMenuSelection = row;
    this.onContextMenuSelected(event);
  }

  onContextMenuSelected(event: MouseEvent, data: T = null): void {
    event.preventDefault();
    const resizer = (event.target as HTMLElement).closest('.pt-column-resizer');
    if (resizer) {
      const resizedColumnName = resizer.closest('[data-column]')?.getAttribute('data-column');
      if (resizedColumnName) {
        this.openFreezeMenu(event, resizedColumnName);
        return;
      }
    }
    const headerCell = (event.target as HTMLElement).closest('[data-column]');
    if (headerCell) {
      // W przyszłości można rozszerzyć i dodać menu kontekstowe dla nagłówka.
      const columnName = headerCell.getAttribute('data-column');
      if (columnName && columnName !== 'options' && !columnName.startsWith('header-'))
        this.unsort(columnName);
    } else {

      if (data != null) {
        // ścieżka z komórki ⋮ — jak przy prawokliku: wiersz z zaznaczonej grupy nie zwija zaznaczenia
        // do siebie, bo menu ma działać na wszystkie zaznaczone wiersze.
        if (!this.isRowInSelection(data))
          this.selection = data;
        this.contextMenuSelection = data;
      } else {
        // ścieżka prawokliku — contextMenuSelection ustawione w onRowRightClick na kliknięty wiersz.
        // Prawy klik poza wierszem (tło tabeli, "Brak danych") łapie się na (contextmenu) samego
        // p-table, bez contextMenuSelection - nie ma czego pokazać i nie ruszamy zaznaczenia.
        if (this.contextMenuSelection == null) {
          this.rowContextMenu?.close();
          return;
        }
        // Jeśli jest częścią zaznaczenia wielu wierszy, menu działa na całe zaznaczenie; inaczej na ten wiersz.
        const isContextArray = Array.isArray(this.contextMenuSelection);
        const isContextSelectionInSelected = !isContextArray && this.isRowInSelection(this.contextMenuSelection as T);

        this.selection = isContextSelectionInSelected ? this.selectedElement : this.contextMenuSelection;
      }
      // Bez tej strażniczki buildMenuOptions dostawałby puste zaznaczenie i wysypywał się
      // w callbackach zakładających realny wiersz.
      const hasSelection = Array.isArray(this.selectedElement)
        ? this.selectedElement.length > 0
        : this.selectedElement != null;
      if (!hasSelection) {
        return;
      }
      this.invalidateMenuOptionsCache(this.selectedElement);
      const shouldDisplay = hasSelection && this.shouldShowSelectOptions(this.selectedElement);

      if (shouldDisplay) {
        this.rowContextMenu?.open(event, this.contentMenuOptions);
      }
      else {
        this.rowContextMenu?.close();
      }
    }
  }

  private unsort(columnName: string): void {
    const sort = this.multiSortMeta.find(meta => meta.field === columnName);
    if (sort) {
      this.table.multiSortMeta = this.multiSortMeta = this.multiSortMeta.filter(meta => meta.field !== columnName);
      this.table.sortMultiple();
    }
  }

  private getRowPk(row: T): any {
    return this.dataPrimaryKey ? row?.[this.dataPrimaryKey] : row;
  }

  // Porównanie po kluczu głównym, nie po referencji: w trybie websocket store podmienia obiekty wierszy
  // przy każdej porcji danych, więc instancje w selectedElement bywają starsze niż wyświetlane.
  private isRowInSelection(row: T): boolean {
    if (!Array.isArray(this.selectedElement) || row == null) return false;
    const pk = this.getRowPk(row);
    return this.selectedElement.some(selected => this.getRowPk(selected) === pk);
  }

  get allPageRowsSelected(): boolean {
    const flat = this.dataSubject?.value;
    if (!flat?.length) return false;
    const dataRows = (this.groupBy ? flat.filter(r => !(r instanceof GroupedData)) : flat) as T[];
    if (!dataRows.length) return false;
    const selectedKeys = new Set((this.selection as T[]).map(r => this.getRowPk(r)));
    return dataRows.every(row => selectedKeys.has(this.getRowPk(row)));
  }

  toggleSelectAllPageRows(): void {
    const flat = (this.dataSubject?.value ?? []);
    const dataRows = (this.groupBy ? flat.filter(r => !(r instanceof GroupedData)) : flat) as T[];
    const selected = this.selection as T[];
    if (this.allPageRowsSelected) {
      const pageKeys = new Set(dataRows.map(r => this.getRowPk(r)));
      this.selection = selected.filter(r => !pageKeys.has(this.getRowPk(r)));
    } else {
      const selectedKeys = new Set(selected.map(r => this.getRowPk(r)));
      this.selection = [...selected, ...dataRows.filter(row => !selectedKeys.has(this.getRowPk(row)))];
    }
  }

  // Zaznaczenie z zewnątrz (np. klik w marker) też przestawia kotwicę ciągu - shift + strzałka ma
  // rozciągać zaznaczenie od wskazanego wiersza.
  select(data: T | T[]): void {
    this.selection = data;
    this.rangeAnchorKey = this.getRowPk(Array.isArray(data) ? data[0] : data);
    // TODO scroll to selected
  }

  // Zdjęcie zaznaczenia z zewnątrz. Kotwica ciągu znika razem z nim - shift + strzałka zaczyna wtedy od
  // wiersza pod fokusem.
  clearSelection(): void {
    this.selection = [];
    this.rangeAnchorKey = null;
  }

  selectAndScroll(data: T, smooth: boolean = false): void {
    this.selection = data;
    this.rangeAnchorKey = this.getRowPk(data);
    this.scrollToRow(data, smooth);
  }

  private scrollToRow(row: T, smooth: boolean): void {
    const index = this.table._value?.findIndex(element => element === row);
    if (!(index >= 0))
      return;
    if (smooth && this.scrollToIndexSmoothly(index))
      return;
    this.table.scrollToVirtualIndex(index);
  }

  // scrollToVirtualIndex przeskakuje natychmiast i przykleja wiersz do górnej krawędzi, więc scrollTop
  // liczymy sami i celujemy w środek widoku. false = nie było z czego policzyć, wołający robi skok.
  private scrollToIndexSmoothly(index: number): boolean {
    const viewport = this.getTableScrollWrapper();
    const rowHeight = this.table?.virtualRowHeight;
    if (viewport == null || !rowHeight)
      return false;
    const centered = index * rowHeight - Math.max(0, (viewport.clientHeight - rowHeight) / 2);
    const maxTop = Math.max(0, viewport.scrollHeight - viewport.clientHeight);
    viewport.scrollTo({ top: Math.min(Math.max(0, centered), maxTop), behavior: 'smooth' });
    return true;
  }

  // Dla wywołań, które mają tylko klucz wiersza (np. numer boczny z mapy), a nie jego instancję -
  // selectAndScroll porównuje referencje.
  selectAndScrollBy(predicate: (row: T) => boolean, smooth: boolean = false): void {
    const row = (this.table?._value as T[])?.find(element => predicate(element));
    if (row != null)
      this.selectAndScroll(row, smooth);
  }

  // Dokładanie do zaznaczenia zamiast podmiany - dla wskazań z Ctrl; wskazany po raz drugi wypada z
  // zaznaczenia. Porównanie po kluczu głównym, nie po referencji (patrz isRowInSelection).
  toggleAndScrollBy(predicate: (row: T) => boolean, smooth: boolean = false): void {
    const row = (this.table?._value as T[])?.find(element => predicate(element));
    if (row == null)
      return;
    if (this.selectionType !== 'multiple') {
      this.selectAndScroll(row, smooth);
      return;
    }
    const key = this.getRowPk(row);
    const selected = (this.selection as T[]).filter(element => element != null);
    const remaining = selected.filter(element => this.getRowPk(element) !== key);
    if (remaining.length === selected.length) {
      this.selection = [...selected, row];
      this.rangeAnchorKey = key;
      this.scrollToRow(row, smooth);
    } else {
      this.selection = remaining;
      this.rangeAnchorKey = remaining.length === 0 ? null : this.getRowPk(remaining[remaining.length - 1]);
    }
  }

  // Wskazanie z zewnątrz całej grupy wierszy naraz (marker koncentracji). Przewijamy do pierwszego;
  // przy zaznaczaniu pojedynczym zostaje tylko on.
  selectAndScrollManyBy(predicate: (row: T) => boolean, smooth: boolean = false): void {
    const rows = ((this.table?._value ?? []) as T[]).filter(element => predicate(element));
    if (rows.length === 0)
      return;
    if (this.selectionType !== 'multiple') {
      this.selectAndScroll(rows[0], smooth);
      return;
    }
    this.selection = rows;
    this.rangeAnchorKey = this.getRowPk(rows[0]);
    this.scrollToRow(rows[0], smooth);
  }

  setExpandableRowDataSources(dataSourceCollection: DataSourceCollection): void {
    this.expandableRowDataSources = dataSourceCollection;
  }

  private onHideColumns(namesToVisibility: ColumnVisabilityPositionSetting[]): void {
    this.tableColumnsService.updateDataTableColumnsByPositonAndVisability(this.dataSource.name, namesToVisibility);
    this.setColumns(this.getCurrentColumnsSettings());
    this.changeDetector.markForCheck();
  }

  onSelectGroupingColumn(columnName?: string | null): void {
    this.tableColumnsService.updateGroupingColumn(this.dataSource.name, columnName);
    this.groupBy = columnName;
    this.dataSource.groupBy = columnName;
    this.cachedGroupByField = columnName ? this.findFieldByName(columnName) : null;
    this.expandedGroups.clear();
    this.groupedData = null;
    this.setupTableViewRefresh();
  }

  private setupTableViewRefresh(): void {
    this.tempMultisortMeta = this.table?.multiSortMeta;
    this.tempPageFirst = this.table?.first;
    this.tempPageRows = this.table?.rows;
    this.displayTable = false;
    this.changeDetector.markForCheck();
    setTimeout(() => {
      this.displayTable = true;
      this.changeDetector.markForCheck();
    });
  }

  private onResetSettings(): void {
    this.groupBy = null;
    this.setFrozenColumnsCount(null);
    this.tableColumnsService.resetColumnsSettings(this.dataSource.name);
    this.dataSource.resetFieldsToInit();
    this.setColumns(this.getInitialColumnsSettings());
    this.displayFooter = false;
    this.refreshSummary();
  }

  private subscribeResizeSubject(): void {
    this.resizeSubject
      .pipe(
        debounceTime(50),
        takeUntil(this.unsubscribeChanges$)
      )
      .subscribe(height => {
        this.containerHeight = height;
        this.setUpTableScrollHeight();
      });
  }

  private setUpTableScrollHeight(): void {
    let scrollHeight: number = 0;
    scrollHeight = this.containerHeight - 30;
    if (this.paginatorVisible)
      scrollHeight -= 25;
    if (this.displayFooter)
      scrollHeight -= 30;
    if (this.columnFilterEnabled && this.columnFilterVisible)
      scrollHeight -= 45;
    if (this.groupingHeaderEnabled)
      scrollHeight -= 30;
    if (this.captionVisible)
      scrollHeight -= 30;
    if (this.showMinimapValue && this.minimapUserVisible)
      scrollHeight -= this.minimapHeight + 1;
    this.tableScrollHeight = (scrollHeight) + 'px';
    this.resetBufferSize();
    this.changeDetector.markForCheck();
    // Odjęte wyżej stałe są przybliżeniem realnych wysokości elementów tabeli,
    // więc po wyrenderowaniu dorównujemy wysokość do faktycznie dostępnego miejsca.
    requestAnimationFrame(() => this.correctTableScrollHeight());
  }

  // Wołane z requestAnimationFrame (po detekcji zmian, przed paintem) i kończone synchronicznym
  // detectChanges() - korekta trafia do tej samej klatki co układ bazowy, bez widocznego skoku.
  private correctTableScrollHeight(): void {
    if ((this.changeDetector as { destroyed?: boolean }).destroyed) return;
    const host = this.elementRef.nativeElement as HTMLElement;
    // Gdy aktywny układ z minimapą, .table-ptable-wrapper to bezpośredni rodzic p-table —
    // mierzymy gap tylko w jego obrębie, żeby nie wchłonąć wysokości zajętej przez minimap.
    // Bez minimapy (.table-ptable-wrapper nie istnieje) cofamy się do host.parentElement.
    const container = host.querySelector<HTMLElement>('.table-ptable-wrapper') ?? host.parentElement;
    const datatable = host.querySelector<HTMLElement>('.p-datatable');
    const current = Number.parseInt(this.tableScrollHeight ?? '');
    if (!container || !datatable || !Number.isFinite(current)) return;
    const gap = Math.round(container.getBoundingClientRect().bottom - datatable.getBoundingClientRect().bottom);
    if (gap !== 0) {
      this.tableScrollHeight = (current + gap) + 'px';
      this.resetBufferSize();
      this.changeDetector.detectChanges();
      this.updateNavScrollState();
    }
  }

  getInitialColumnWidth(columnName: string): number {
    if (columnName === 'options') return PrimeTableComponent.OPTIONS_COLUMN_WIDTH;
    return this.getColumnWidths().get(columnName) ?? PrimeTableComponent.DEFAULT_COLUMN_WIDTH;
  }

  // Szerokość dla colgroup — każda kolumna dostaje DOKŁADNIE swoją zadaną szerokość (px), także ostatnia.
  getColgroupWidthStyle(columnName: string): { [key: string]: string } {
    return { width: this.getInitialColumnWidth(columnName) + 'px' };
  }

  // Stałe szerokości bez rozciągania: każda kolumna trzyma swoją zapisaną szerokość, a "options" stałe 30px.
  // Tabela NIE wypełnia kontenera na siłę (CSS: table sizuje się do sumy kolumn) — gdy kolumn jest mało,
  // po prawej zostaje puste miejsce, a przy przepełnieniu pojawia się poziomy scroll. Dzięki temu kolumny
  // nigdy nie zmieniają szerokości samoczynnie (przy hover czy zmianie rozmiaru innych kolumn), bo nie ma
  // wolnej przestrzeni, którą przeglądarka mogłaby rozdzielać proporcjonalnie przy table-layout: fixed.
  //
  // Wynik jest cache'owany po referencji zestawu kolumn i wersji ustawień (columnSettingsVersion), więc
  // zwykłe wykrywanie zmian (np. matTooltip na hover) zwraca ten sam Map bez przeliczeń i migotania szerokości.
  private getColumnWidths(): Map<string, number> {
    const cache = this.columnWidthsCache;
    if (cache && cache.columns === this.displayedColumns && cache.settingsVersion === this.columnSettingsVersion)
      return cache.widths;

    const settings = new Map(this.getCurrentColumnsSettings().map(s => [s.name, s]));
    const widths = new Map<string, number>();
    for (const column of this.displayedColumns ?? []) {
      if (column.name === 'options')
        continue;
      // Kolumna przypięta nie ma wpisu w ustawieniach - szerokość bierze wprost z definicji.
      widths.set(column.name, column.locked
        ? (column.defaultWidth ?? PrimeTableComponent.DEFAULT_COLUMN_WIDTH)
        : (settings.get(column.name)?.width ?? PrimeTableComponent.DEFAULT_COLUMN_WIDTH));
    }

    this.columnWidthsCache = {
      columns: this.displayedColumns,
      settingsVersion: this.columnSettingsVersion,
      widths
    };
    this.updateFrozenLeftOffsets(widths);
    this.updateTableWidth(widths);
    return widths;
  }

  // Ustawia --pt-table-width = suma szerokości kolumn (options + kolumny danych) jako definitywną
  // szerokość w px dla wszystkich trzech tabel scrollable view (nagłówek/dane/stopka — patrz SCSS).
  // Dzięki temu przy table-layout: fixed każda kolumna renderuje się dokładnie na szerokość z colgroup:
  // przy sumie mniejszej od kontenera zostaje puste miejsce (zamiast proporcjonalnego rozciągania
  // wszystkich kolumn), a przy większej pojawia się poziomy scroll.
  private updateTableWidth(widths: Map<string, number>): void {
    let total = this.checkIfOptionColumnExists() ? PrimeTableComponent.OPTIONS_COLUMN_WIDTH : 0;
    for (const width of widths.values())
      total += width;
    this.elementRef.nativeElement.style.setProperty('--pt-table-width', total + 'px');
  }

  checkIfOptionColumnExists(): boolean {
    return this.displayedColumns.some(column => column.name === 'options');
  }

  // Łączna liczba przypiętych komórek od lewej (razem z kolumną "options") — steruje klasą pt-frozen-N,
  // dla której style generowane są do MAX_FROZEN_COLUMNS + 1. Uwzględnia limit szerokości strefy,
  // więc zwężenie okna lub poszerzenie kolumn samoczynnie zmniejsza liczbę efektywnie przypiętych.
  get frozenCellsTotal(): number {
    if (this.frozenColumnsCount == null || !this.displayedColumns?.length)
      return 0;
    const requested = Math.min(Math.max(this.frozenColumnsCount, 0), PrimeTableComponent.MAX_FROZEN_COLUMNS, this.visibleColumnsCount);
    return this.clampFrozenCountToZoneWidth(requested) + (this.checkIfOptionColumnExists() ? 1 : 0);
  }

  // Przycina liczbę przypiętych kolumn danych tak, by strefa (razem z "options") zmieściła się
  // w FROZEN_ZONE_MAX_VIEWPORT_RATIO szerokości kontenera tabeli.
  private clampFrozenCountToZoneWidth(count: number): number {
    if (count <= 0)
      return Math.max(count, 0);
    if (this.tableContainerWidth <= 0 || !this.displayedColumns?.length)
      return count;
    const maxZoneWidth = this.tableContainerWidth * PrimeTableComponent.FROZEN_ZONE_MAX_VIEWPORT_RATIO;
    const cols = this.displayedColumns;
    const hasOptions = this.checkIfOptionColumnExists();
    let zoneWidth = hasOptions ? PrimeTableComponent.OPTIONS_COLUMN_WIDTH : 0;
    let fitting = 0;
    for (let i = hasOptions ? 1 : 0; fitting < count && i < cols.length; i++) {
      zoneWidth += this.getInitialColumnWidth(cols[i].name);
      if (zoneWidth > maxZoneWidth)
        break;
      fitting++;
    }
    return fitting;
  }

  // Kolumn przypiętych na stałe (column.locked) lista kolumn nie pokazuje, ale frozenColumnsCount liczy
  // się po displayedColumns, czyli razem z nimi - lista musi tę różnicę znać.
  get lockedColumnsCount(): number {
    return this.displayedColumns?.filter(column => column.locked).length ?? 0;
  }

  get frozenWrapperClass(): string {
    const total = this.frozenCellsTotal;
    return total > 0 ? 'pt-frozen pt-frozen-' + total : '';
  }

  setFrozenColumnsCount(count: number | null): void {
    const normalized = count == null
      ? null
      : this.clampFrozenCountToZoneWidth(Math.max(0, Math.min(count, PrimeTableComponent.MAX_FROZEN_COLUMNS)));
    if (normalized === this.frozenColumnsCount)
      return;
    this.frozenColumnsCount = normalized;
    if (this.dataSource)
      this.tableColumnsService.updateFrozenColumnsCount(this.dataSource.name, normalized);
    // Po przełączeniu trybu nagłówek/stopka muszą przejść między synchronizacją przez scrollLeft
    // (przypinanie aktywne) a ujemnym margin-left boksu (nieaktywne) — patrz syncHeaderToScrollLeft.
    // setTimeout: najpierw musi zaaplikować się klasa pt-frozen (neutralizacja marginesu).
    setTimeout(() => this.syncFrozenHeaderScroll());
  }

  private syncFrozenHeaderScroll(): void {
    const scrollLeft = this.getScrollableBody()?.scrollLeft ?? 0;
    if (this.frozenCellsTotal <= 0) {
      // Powrót do trybu margin-left: realny scrollLeft nagłówka/stopki musi wrócić do zera,
      // bo syncHeaderToScrollLeft już go nie aktualizuje (rusza tylko margines boksu).
      const root = this.elementRef.nativeElement as HTMLElement;
      root.querySelectorAll('.p-datatable-scrollable-header, .p-datatable-scrollable-footer')
        .forEach(el => (el as HTMLElement).scrollLeft = 0);
    }
    this.syncHeaderToScrollLeft(scrollLeft);
  }

  // Menu PPM na uchwycie zmiany szerokości kolumny w nagłówku — przypina kolumny od lewej
  // do klikniętej włącznie (semantyka "freeze panes").
  openFreezeMenu(event: MouseEvent, columnName: string): void {
    const index = this.displayedColumns.findIndex(c => c.name === columnName);
    if (index < 1)
      return;
    // pozycja nieaktywna, gdy limit liczby lub szerokości strefy nie pozwala przypiąć aż dotąd
    const canFreezeHere = index <= PrimeTableComponent.MAX_FROZEN_COLUMNS && this.clampFrozenCountToZoneWidth(index) === index;
    this.freezeMenuItems = [
      {
        label: this.translate.instant('freezeColumnsHere'),
        icon: 'mi mi-keep',
        disabled: !canFreezeHere,
        command: () => this.setFrozenColumnsCount(index)
      },
      {
        label: this.translate.instant('unfreezeColumns'),
        icon: 'mi mi-keep_off',
        disabled: this.frozenColumnsCount == null,
        command: () => this.setFrozenColumnsCount(null)
      }
    ];
    this.freezeContextMenu?.open(event, this.freezeMenuItems);
  }

  get dataKeyForTable(): string | null {
    return this.useRowIdAsPK ? 'rowId' : this.dataPrimaryKey;
  }

  isAdditionalInfo(data: any): boolean {
    return data instanceof AdditionalInfoImpl;
  }

  private createGroupingHeaders(columns: ColumnDefinition[]): void {
    const groupingHeaders: GroupingHeader[] = [];
    const noGroupingHeaders: GroupingHeader[] = [];
    const uniqueNames: string[] = [];
    columns.forEach((column: ColumnDefinition) => {
      if (!column.groupingHeaderName)
        noGroupingHeaders.push({ name: '', colspan: 1 });
      else
        if (!uniqueNames.includes(column.groupingHeaderName))
          uniqueNames.push(column.groupingHeaderName);
    });
    uniqueNames.forEach((name: string) => {
      const colspan: number = columns.filter((column: ColumnDefinition) => column.groupingHeaderName === name).length;
      groupingHeaders.push({ name, colspan });
    });
    this.groupingHeaders = groupingHeaders.sort((a, b) => a.colspan - b.colspan);
    this.groupingHeaders = [...noGroupingHeaders, ...groupingHeaders];
  }

  sortColumnsByGroupingHeaders(columns: ColumnDefinition[]): ColumnDefinition[] {
    const groupingColumns: ColumnDefinition[] = [];
    const noGroupingColumns = columns.filter((col: ColumnDefinition) => !col.groupingHeaderName);
    this.groupingHeaders.forEach((groupingHeader: GroupingHeader) => {
      groupingColumns.push(...columns.filter((column: ColumnDefinition) => column?.groupingHeaderName === groupingHeader.name));
    });
    return [...noGroupingColumns, ...groupingColumns];
  }

  private refreshSummary(): void {
    this.summaryObject = null;
    this.changeDetector.markForCheck();
    if (this.summaryEnabled && this.displayFooter) {
      this.summarySubscription = this.dataOptionsConnectorService.getActiveFilters().pipe(
        takeUntil(this.unsubscribeChanges$),
        filter(resp => resp != null),
        switchMap((reqCriteria: RequestCriteria) => {
          if (this.summarySubscription)
            this.summarySubscription.unsubscribe();
          this.isSummaryLoading = true;
          return this.dataLoader.getSummary({
            data: reqCriteria,
            summaryFunctions: this.createSummaryRequest()
          });
        })
      ).subscribe(resp => {
        this.isSummaryLoading = false;
        this.summaryObject = resp;
        this.changeDetector.detectChanges();
      });
    }
  }

  private createSummaryRequest(): SummaryFunctions {
    if (this.summaryObject)
      return this.summaryObject.createSummaryRequest(this.summaryType);
    return { option: this.summaryType };
  }

  openSummarySettings(): void {
    if (!this.isSummaryLoading) {
      this.matDialog.open(SummarySettingsDialogComponent, { data: this.summaryType })
        .afterClosed().subscribe((type: SummaryType) => {
          if (type == null) {
            this.summaryType = null;
            this.displayFooter = false;
          } else if (type != null && this.summaryType != type) {
            this.summaryType = type;
            this.refreshSummary();
          }
        });
    }
  }

  findFieldByName(name: string): ValueField {
    return this.dataSource.getValueFields().find((field: ValueField) => name == field.fieldName || name == field.name);
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (event.ctrlKey && event.key === 'a' && this.selectionType === 'multiple') {
      event.preventDefault();
      this.toggleSelectAllPageRows();
    }
  }

  trackBy = (index: number, item: any): any => {
    if (item instanceof GroupedData) return '__group__' + item.groupName;
    return item?.[this.dataPrimaryKey] ?? index;
  };

  getGroupSummaryValues(data: T[]): string[] {
    const aggregator = this.groupSummaryConfig.aggregators?.[this.groupBy];
    if (aggregator) return data?.length ? aggregator(data) : [];
    const field = this.groupSummaryConfig.fields?.[this.groupBy];
    if (!field || !data?.length) return [];
    return [...new Set(data.map(d => d[field]).filter(v => v != null && v !== ''))];
  }

  hasGroupSummaryAggregator(): boolean {
    return !!(this.groupSummaryConfig.aggregators && this.groupSummaryConfig.aggregators[this.groupBy]);
  }

  hasGroupSummaryField(): boolean {
    return !!(this.groupSummaryConfig.fields && this.groupSummaryConfig.fields[this.groupBy])
      && !this.hasGroupSummaryAggregator();
  }

  getGroupSummaryTooltip(): string {
    return this.groupSummaryConfig.tooltips && this.groupSummaryConfig.tooltips[this.groupBy];
  }

  // zwiększa bufor na całość danych, gdy nie ma ich dużo (nie przekraczają czterokrotności virtual scroll), w celu wyeliminowania "lagowania" tabeli.
  private resetBufferSize(): void {
    if (this.table?.scrollHeight && this.table?.virtualRowHeight) {
      const total = this.groupBy
        ? (this.dataSubject?.value?.length ?? 0)
        : (this.showPagination && this.currentDataCount > 0 ? this.paginationSize : this.table.totalRecords);
      if (total > 0) {
        const scrollHeight = Number.parseInt(this.table?.scrollHeight?.replace('px', ''));
        const canLoadAll = this.table.virtualRowHeight * total / scrollHeight < 4;
        this.table.minBufferPx = canLoadAll ? (this.table.virtualRowHeight * total) - this.table.virtualRowHeight : 100;
        this.table.maxBufferPx = canLoadAll ? this.table.virtualRowHeight * total : 200;
      }
    }
  }

  goToPage(page: number): void {
    if (this.currentDataCount === 0) return;
    this.pageInput = Math.max(1, page ?? 0);
    this.table.first = (this.pageInput - 1) * this.paginationSize;
    this.updateDataSourceData();
  }

  // Kursory góra/dół przesuwają zaznaczenie, a nie tylko fokus DOM (pSelectableRow robi samo focus()).
  // Z shiftem wiersz dokłada się do zaznaczenia, bez shiftu je zastępuje.
  rowKey(event: KeyboardEvent, _row: T, index: number): void {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown')
      return;
    const directionOffs = event.key === 'ArrowDown' ? 1 : -1;
    const targetIndex = index + directionOffs;
    const focused = this.table.value?.[targetIndex];
    if (!focused)
      return;
    // Fokus przy shifcie przesuwamy sami: pSelectableRow nasłuchuje "keydown.arrowdown", co w Angularze
    // znaczy strzałkę BEZ modyfikatorów, więc z shiftem jego obsługa nie dochodzi do skutku.
    if (event.shiftKey) {
      // preventDefault też jest nasz - inaczej strzałka z shiftem przewija kontener tabeli.
      event.preventDefault();
      this.focusSiblingRow(event.currentTarget as HTMLElement, directionOffs);
    }
    this.rowFocus.emit(focused);
    // Nagłówek grupy nie jest wierszem danych - fokus przez niego przechodzi, ale zaznaczyć się nie da.
    if (focused instanceof GroupedData)
      return;
    if (this.selectionType !== 'multiple') {
      this.selection = focused;
      this.rangeAnchorKey = this.getRowPk(focused);
      return;
    }
    if (!event.shiftKey) {
      this.selection = [focused];
      this.rangeAnchorKey = this.getRowPk(focused);
      return;
    }
    this.selection = this.rowsBetween(this.resolveRangeAnchorIndex(index), targetIndex);
  }

  // Wiersz, od którego liczy się ciąg budowany shiftem. Klucz główny, nie indeks, bo sortowanie, filtr
  // i świeża porcja danych przestawiają wiersze pod spodem.
  private rangeAnchorKey: any = null;

  // Bez kotwicy (pierwszy shift po kliknięciu wiersza) albo gdy jej wiersz wypadł z widoku po filtrze:
  // zaczynamy ciąg od wiersza, na którym stoi fokus.
  private resolveRangeAnchorIndex(focusedIndex: number): number {
    if (this.rangeAnchorKey == null)
      return focusedIndex;
    const anchorIndex = (this.table.value as T[])?.findIndex(row =>
      !(row instanceof GroupedData) && this.getRowPk(row) === this.rangeAnchorKey);
    return anchorIndex != null && anchorIndex >= 0 ? anchorIndex : focusedIndex;
  }

  // Ciąg od kotwicy do wiersza pod fokusem, liczony od nowa w obie strony - dokładanie do poprzedniego
  // zaznaczenia nie umiałoby go skurczyć przy powrocie po własnych śladach.
  private rowsBetween(fromIndex: number, toIndex: number): T[] {
    const firstIndex = Math.min(fromIndex, toIndex);
    const lastIndex = Math.max(fromIndex, toIndex);
    return ((this.table.value ?? []) as T[])
      .slice(firstIndex, lastIndex + 1)
      .filter(row => !(row instanceof GroupedData));
  }

  // Fokus na sąsiednim wierszu, tą samą drogą co pSelectableRow (klasa p-selectable-row), żeby
  // klawiatura zachowywała się tak samo z shiftem i bez.
  private focusSiblingRow(row: HTMLElement, directionOffs: number): void {
    let sibling = directionOffs > 0 ? row?.nextElementSibling : row?.previousElementSibling;
    while (sibling != null && !sibling.classList.contains('p-selectable-row'))
      sibling = directionOffs > 0 ? sibling.nextElementSibling : sibling.previousElementSibling;
    (sibling as HTMLElement)?.focus();
  }

  private getTableScrollWrapper(): HTMLElement | null {
    return this.table?.el?.nativeElement?.querySelector('cdk-virtual-scroll-viewport');
  }

  // Scroll jest uznawany za ruch użytkownika tylko wtedy, gdy tuż przed nim był realny
  // sygnał wejściowy (wheel/touch/pasek/klawisz). Programowe i layoutowe korekty scrollTop
  // (np. checkViewportSize po wstawieniu wierszy) z definicji nie są ruchem użytkownika.
  // Viewport potrafi pojawić się później niż setupTable(), stąd ponawianie prób podpięcia.
  private setupUserScrollListener(attempt: number = 0): void {
    this.removeUserScrollListener();
    const w = this.getTableScrollWrapper();
    if (!w) {
      if (attempt < 20) setTimeout(() => this.setupUserScrollListener(attempt + 1), 250);
      return;
    }
    this.zone.runOutsideAngular(() => {
      const add = (target: EventTarget, type: string, fn: EventListener, opts?: AddEventListenerOptions): void => {
        target.addEventListener(type, fn, opts);
        this.userInputListeners.push({ target, type, fn });
      };
      const markInput = (windowMs: number) => () => this.userInputUntil = performance.now() + windowMs;
      add(w, 'wheel', markInput(250), { passive: true });
      // dłuższe okno - scroll bezwładnościowy po puszczeniu palca nie generuje touchmove
      add(w, 'touchmove', markInput(1200), { passive: true });
      add(w, 'keydown', markInput(250));
      add(w, 'mousedown', () => this.pointerDownOnTable = true);
      add(window, 'mouseup', () => this.pointerDownOnTable = false);
      add(w, 'scroll', () => {
        // Skeleton (opt-in): wzór na pseudo-elemencie viewportu widoczny tylko tam, gdzie
        // nie ma wyrenderowanej treści (content-wrapper ma nieprzezroczyste tło i zasłania
        // go). Segmenty generowane z rzeczywistych szerokości kolumn przy rozpoczęciu
        // "zrywu" przewijania; przesunięcie poziome tylko przy zmianie scrollLeft.
        // Przy "Brak danych" nie ma czego "chować" pod wzorem (nie ma niewyrenderowanej
        // treści ani nic się nie ładuje) - a bez limitu wysokości (updateSkeletonMaxHeight
        // przy zerowej liczbie wierszy usuwa --sk-max-h) wzór rysowałby się na całą wysokość
        // wiersza "Brak danych" i nigdy nie miał powodu, żeby sensownie zniknąć.
        if (this.scrollSkeletonEnabled && this.visibleRowCountSubject.value > 0) {
          if (!w.classList.contains('vs-skeleton-active')) {
            this.applySkeletonColumnsGradient(w);
            w.classList.add('vs-skeleton-active');
          }
          if (w.scrollLeft !== this.skeletonScrollLeft) {
            this.skeletonScrollLeft = w.scrollLeft;
            w.style.setProperty('--sk-col-x', `${-w.scrollLeft}px`);
          }
          // Wzór nie dalej niż do końca danych - przy krótkiej liście (np. 5 wierszy
          // w widoku na 15) animowany skeleton pod danymi sugerowałby, że coś się
          // jeszcze ładuje. Ten sam limit obowiązuje też na czas ładowania (patrz
          // updateSkeletonMaxHeight) - inaczej skeleton "na całą wysokość" wjeżdżał pod
          // już wyrenderowane, zwinięte grupy.
          this.updateSkeletonMaxHeight(w);
          if (this.skeletonHideTimer != null) clearTimeout(this.skeletonHideTimer);
          this.skeletonHideTimer = setTimeout(() => {
            if (!this.skeletonLoadingActive) w.classList.remove('vs-skeleton-active');
          }, 4000);
        }
        // userScroll dotyczy PIONOWEJ pozycji (tryb "podążania" u konsumenta: getFirstSortMeta + isAtEdge,
        // a isAtEdge czyta geometrię = wymusza reflow). Emitujemy więc tylko przy realnej zmianie scrollTop:
        // zdarzenie scroll odpala się też przy przewijaniu POZIOMYM, a emisja obciążałaby wtedy wątek
        // główny dokładnie tam, gdzie nagłówek musi nadążać za danymi.
        if (w.scrollTop !== this.lastUserScrollTop) {
          this.lastUserScrollTop = w.scrollTop;
          if (this.pointerDownOnTable || performance.now() < this.userInputUntil) {
            this.userScroll.emit(w.scrollTop);
          }
        }
      });
    });
  }

  private removeUserScrollListener(): void {
    this.userInputListeners.forEach(l => l.target.removeEventListener(l.type, l.fn));
    this.userInputListeners = [];
    if (this.skeletonHideTimer != null) {
      clearTimeout(this.skeletonHideTimer);
      this.skeletonHideTimer = null;
    }
    this.skeletonScrollLeft = -1;
  }

  // Skeleton "ładowania": na czas ładowania danych wzór pokrywa tabelę do końca już
  // znanej treści (patrz updateSkeletonMaxHeight - uwzględnia zwinięte grupy), a komunikat
  // o braku danych jest ukryty (CSS) - pokazuje się dopiero po zakończeniu ładowania,
  // gdy skeleton gaśnie.
  private applyLoadingSkeleton(attempt: number = 0): void {
    const w = this.getTableScrollWrapper();
    if (!w) {
      if (this.skeletonLoadingActive && attempt < 20) {
        setTimeout(() => this.applyLoadingSkeleton(attempt + 1), 250);
      }
      return;
    }
    if (this.skeletonLoadingActive) {
      this.applySkeletonColumnsGradient(w);
      this.updateSkeletonMaxHeight(w);
      w.classList.add('vs-skeleton-active');
    } else {
      w.classList.remove('vs-skeleton-active');
    }
  }

  // Limit wysokości wzoru skeletonu do końca faktycznie znanej treści (z uwzględnieniem
  // zwiniętych/rozwiniętych grup). Domyślnie liczone z table._value (już spłaszczona,
  // uwzględniająca grupowanie lista) - ale zaraz po emitFlatData() ta wartość jeszcze
  // nie jest aktualna (async pipe zaktualizuje [value] dopiero przy najbliższym cyklu CD,
  // a rebuildFlatData() celowo działa poza zoną), więc wywołujący może podać świeżą
  // długość wprost, żeby nie czytać jeszcze nieodświeżonego table._value.
  private updateSkeletonMaxHeight(w: HTMLElement, rowsOverride?: number): void {
    const rows = rowsOverride ?? this.table?._value?.length ?? 0;
    if (rows > 0) {
      w.style.setProperty('--sk-max-h', `${Math.max(0, rows * this.getNavRowHeight() - w.scrollTop)}px`);
    } else {
      w.style.removeProperty('--sk-max-h');
    }
  }

  // Gradient segmentów skeletonu dopasowany do rzeczywistych szerokości kolumn
  // (stan z chwili rozpoczęcia przewijania; zmiany szerokości podchwyci kolejny zryw).
  // Szerokość całości idzie w --sk-cols-w, żeby background-size objął pełną tabelę,
  // a nie tylko widoczny wycinek - inaczej przesuwanie o -scrollLeft zawijałoby wzór.
  private applySkeletonColumnsGradient(w: HTMLElement): void {
    const offsets = this.getColumnOffsets();
    const cols = this.displayedColumns ?? [];
    const stops: string[] = [];
    let last = 0;
    let total = 0;
    cols.forEach((col, i) => {
      const width = this.getInitialColumnWidth(col.name);
      total = offsets[i] + width;
      const pad = Math.min(9, width * 0.18);
      const from = Math.round(offsets[i] + pad);
      const to = Math.round(offsets[i] + width - pad);
      if (to - from < 10) return;
      stops.push(`transparent ${last}px ${from}px`);
      stops.push(`rgba(0,0,0,0.10) ${from}px ${to}px`);
      last = to;
    });
    stops.push(`transparent ${last}px`);
    w.style.setProperty('--sk-cols', `linear-gradient(to right, ${stops.join(', ')})`);
    w.style.setProperty('--sk-cols-w', `${Math.max(total, 1)}px`);
  }

  getCurrentData(): T[] {
    return this.table?._value ?? [];
  }

  // Ustawia widok tak, żeby wiersz o podanym indeksie był przy zadanej krawędzi (z kilkoma
  // wierszami kontekstu za nim) albo dokładnie na środku ekranu ('center' - kontekst po
  // obu stronach, bez kombinowania czy bliżej góry, czy dołu; contextRows ignorowane).
  // No-op, gdy pozycja już właściwa (onComplete wywoływane też w tym wypadku - konsument
  // nie musi rozróżniać no-opu od animacji). onComplete: wołany po faktycznym zakończeniu
  // ruchu - używać go zamiast sztywnego opóźnienia do sekwencjonowania kosztownych
  // operacji (np. re-render po scrollu), inaczej taka operacja uruchomiona w połowie
  // glide'u zjada jego klatki.
  scrollToIndexBias(index: number, bias: 'top' | 'bottom' | 'center', contextRows: number = 3,
    behavior: ScrollBehavior = 'smooth', onComplete?: () => void): void {
    const w = this.getTableScrollWrapper();
    if (!w) { onComplete?.(); return; }
    const rowH = this.getNavRowHeight();
    let target: number;
    if (bias === 'top') {
      target = (index - contextRows) * rowH;
    } else if (bias === 'bottom') {
      target = (index + 1 + contextRows) * rowH - w.clientHeight;
    } else {
      target = (index + 0.5) * rowH - w.clientHeight / 2;
    }
    target = Math.max(0, Math.min(target, w.scrollHeight - w.clientHeight));
    if (Math.abs(w.scrollTop - target) < 1) { onComplete?.(); return; }
    this.cancelScrollWatch?.();
    this.cancelScrollWatch = null;
    // Natywny scroll-behavior:smooth (jak scrollIntoView w rejestrze realizacji) -
    // płynność sterowana przez kompozytor przeglądarki, nie przez nasz JS na głównym
    // wątku, więc nie gaśnie pod ciężkim renderem tak jak własna pętla rAF.
    w.scrollTo({ top: target, behavior });
    if (onComplete) {
      if (behavior === 'smooth') {
        this.cancelScrollWatch = this.watchScrollEnd(w, onComplete);
      } else {
        onComplete();
      }
    }
  }

  // 'scrollend' odpala się po faktycznym zatrzymaniu scrolla (wspierane w aktualnych
  // Chrome/Firefox/Edge); timer awaryjny na wypadek braku wsparcia albo sytuacji, w
  // której zdarzenie z jakiegoś powodu nie nadejdzie. Poza strefą Angulara - onComplete
  // sam wchodzi w zone.run(), żeby konsument nie musiał o tym pamiętać. Zwraca funkcję
  // anulującą (odpina listener/timer BEZ wołania onComplete) - do przerwania przez
  // kolejny scroll startujący, zanim ten się dokończy.
  private watchScrollEnd(w: HTMLElement, onComplete: () => void): () => void {
    let cleanup: () => void = () => { };
    const finish = (): void => {
      cleanup();
      this.zone.run(() => onComplete());
    };
    this.zone.runOutsideAngular(() => {
      const onScrollEnd = (): void => finish();
      const fallback = setTimeout(finish, 1000);
      cleanup = () => {
        w.removeEventListener('scrollend', onScrollEnd);
        clearTimeout(fallback);
      };
      w.addEventListener('scrollend', onScrollEnd, { once: true });
    });
    return cleanup;
  }

  isAtEdge(edge: 'top' | 'bottom'): boolean {
    const w = this.getTableScrollWrapper();
    if (!w) return edge === 'top';
    return edge === 'top'
      ? w.scrollTop <= 1
      : w.scrollTop >= w.scrollHeight - w.clientHeight - 1;
  }

  getFirstSortMeta(): SortMeta | null {
    return this.table?.multiSortMeta?.[0] ?? null;
  }

  navFirstColumn(): void {
    const w = this.getTableScrollWrapper();
    if (w) w.scrollLeft = 0;
  }

  navLastColumn(): void {
    const w = this.getTableScrollWrapper();
    if (w) w.scrollLeft = w.scrollWidth - w.clientWidth;
  }

  navColumnPageLeft(): void {
    const w = this.getTableScrollWrapper();
    if (!w) return;
    const frozenWidth = this.frozenZoneWidth();
    // strona = szerokość widocznej, nieprzypiętej części widoku
    const targetContentLeft = Math.max(0, w.scrollLeft + frozenWidth - (w.clientWidth - frozenWidth));
    const offsets = this.getColumnOffsets();
    let newLeft = 0;
    for (const offset of offsets) {
      if (offset <= targetContentLeft) newLeft = offset;
    }
    w.scrollLeft = Math.max(0, newLeft - frozenWidth);
  }

  navColumnPageRight(): void {
    const w = this.getTableScrollWrapper();
    if (!w) return;
    const frozenWidth = this.frozenZoneWidth();
    const contentLeft = w.scrollLeft + frozenWidth;
    const viewRight = w.scrollLeft + w.clientWidth;
    const maxLeft = w.scrollWidth - w.clientWidth;
    const offsets = this.getColumnOffsets();
    let newLeft: number | null = null;
    for (const offset of offsets) {
      if (offset > contentLeft && offset <= viewRight) newLeft = offset;
    }
    if (newLeft === null) {
      for (const offset of offsets) {
        if (offset > contentLeft) { newLeft = offset; break; }
      }
    }
    if (newLeft !== null) w.scrollLeft = Math.min(Math.max(0, newLeft - frozenWidth), maxLeft);
  }

  private getColumnOffsets(): number[] {
    const offsets: number[] = [];
    let cum = 0;
    for (const col of this.displayedColumns ?? []) {
      offsets.push(cum);
      cum += this.getInitialColumnWidth(col.name);
    }
    return offsets;
  }

  // Szerokość strefy przypiętych kolumn. Nawigacja pozioma wyrównuje kolumny do jej prawej
  // krawędzi, bo przypięte kolumny zasłaniają lewy skraj widoku (scrollLeft = offset kolumny
  // minus szerokość strefy).
  private frozenZoneWidth(): number {
    const total = this.frozenCellsTotal;
    if (total <= 0) return 0;
    const cols = this.displayedColumns ?? [];
    let width = 0;
    for (let i = 0; i < total && i < cols.length; i++)
      width += this.getInitialColumnWidth(cols[i].name);
    return width;
  }

  navColumnLeft(): void {
    const w = this.getTableScrollWrapper();
    if (!w) return;
    const frozenWidth = this.frozenZoneWidth();
    const contentLeft = w.scrollLeft + frozenWidth;
    let target = 0;
    for (const offset of this.getColumnOffsets()) {
      if (offset < contentLeft - 1) target = offset;
    }
    w.scrollLeft = Math.max(0, target - frozenWidth);
  }

  navColumnRight(): void {
    const w = this.getTableScrollWrapper();
    if (!w) return;
    const frozenWidth = this.frozenZoneWidth();
    const contentLeft = w.scrollLeft + frozenWidth;
    for (const offset of this.getColumnOffsets()) {
      if (offset > contentLeft + 1) {
        w.scrollLeft = offset - frozenWidth;
        return;
      }
    }
  }

  private getNavRowHeight(): number {
    const vh = (this.table as any)?.virtualRowHeight;
    return (typeof vh === 'number' && vh > 0) ? vh : (parseInt(this.tableRowHeight as any, 10) || 28);
  }

  navFirstRow(): void {
    const w = this.getTableScrollWrapper();
    if (w) w.scrollTop = 0;
  }

  navLastRow(): void {
    const w = this.getTableScrollWrapper();
    if (w) w.scrollTop = w.scrollHeight - w.clientHeight;
  }

  navRowUp(): void {
    const w = this.getTableScrollWrapper();
    if (w) w.scrollTop = Math.max(0, w.scrollTop - this.getNavRowHeight());
  }

  navRowDown(): void {
    const w = this.getTableScrollWrapper();
    if (w) w.scrollTop = Math.min(w.scrollHeight - w.clientHeight, w.scrollTop + this.getNavRowHeight());
  }

  navRowPageUp(): void {
    const w = this.getTableScrollWrapper();
    if (!w) return;
    const h = this.getNavRowHeight();
    const target = Math.max(0, w.scrollTop - w.clientHeight);
    w.scrollTop = Math.floor(target / h) * h;
  }

  navRowPageDown(): void {
    const w = this.getTableScrollWrapper();
    if (!w) return;
    const h = this.getNavRowHeight();
    const target = w.scrollTop + w.clientHeight;
    w.scrollTop = Math.min(Math.floor(target / h) * h, w.scrollHeight - w.clientHeight);
  }

  toggleNavPanel(): void {
    this.navPanelOpen = !this.navPanelOpen;
    if (this.navPanelOpen) {
      setTimeout(() => this.setupNavScrollListener());
    } else {
      this.removeNavScrollListener();
    }
  }

  /** Pokaż/ukryj minimap (przycisk "Widok" na pasku tabeli). */
  toggleMinimap(): void {
    this.minimapUserVisible = !this.minimapUserVisible;
    this.tableColumnsService.saveViewPrefs({ minimap: this.minimapUserVisible });
    this.setUpTableScrollHeight();
    // MinimapDirective is created/destroyed automatically by the *ngIf on its
    // host element (driven by minimapUserVisible) — no manual setup/destroy needed.
  }

  /** Dopasuj wysokość minimap tak, żeby cała szerokość tabeli zmieściła się bez poziomego panowania. */
  fitMinimapToWidth(): void {
    this.minimapDir?.fitToWidth();
  }

  /** Pokaż/ukryj pasek filtrów kolumnowych (przycisk "Widok" na pasku tabeli). */
  toggleColumnFilter(): void {
    this.columnFilterVisible = !this.columnFilterVisible;
    this.tableColumnsService.saveViewPrefs({ filterBar: this.columnFilterVisible });
    this.setUpTableScrollHeight();
  }

  /** Wyczyść wszystkie filtry kolumnowe (skrót z menu "Widok"). */
  clearAllColumnFilters(): void {
    this.columnFilterService.clearAllColumnFilters();
  }

  /**
   * Filtr kolumnowy założony spoza tabeli (menu kontekstowe wiersza, mapa). Wartości zapamiętujemy
   * zawsze, bo kontrolki filtra istnieją tylko przy widocznym wierszu filtrów - przy schowanym odczyta
   * je dopiero ColumnFilterComponent.ngOnInit i sam założy kryterium.
   */
  applyColumnFilterValues(entries: Array<{ name: string; value: any }>): void {
    if (!this.columnFilterEnabled)
      return;
    entries.forEach(({ name, value }) => this.dataOptionsConnectorService.setColumnFilterValue(name, value));
    if (this.columnFilterVisible)
      this.columnFilterService.applyValues(entries);
    else
      this.toggleColumnFilter();
  }

  /** Pokaż/ukryj row ruler — linijkę wierszy (przycisk "Widok" na pasku tabeli). */
  toggleRowRuler(): void {
    this.rowRulerVisible = !this.rowRulerVisible;
    this.tableColumnsService.saveViewPrefs({ ruler: this.rowRulerVisible });
    // RowRulerDirective is created/destroyed automatically by the *ngIf on its
    // host element (driven by rowRulerVisible) — no manual setup/destroy needed.
  }

  /**
   * Pokaż/ukryj pasek paginacji dla tej tabeli (przycisk "Widok", ustawienie per-tabela).
   * Kontroluje wyłącznie widoczność paska — sama paginacja pozostaje aktywna (dane ładują się
   * stronicowane wg dotychczasowych ustawień: liczba wierszy na stronę, bieżąca strona itd.).
   */
  togglePagination(): void {
    this.paginationUserHidden = !this.paginationUserHidden;
    this.tableColumnsService.savePaginationHidden(this.dataSource.name, this.paginationUserHidden);
    this.paginatorVisible = this.showPagination && !this.paginationUserHidden;
    this.setUpTableScrollHeight();
  }

  private updateNavScrollState(): void {
    const w = this.getTableScrollWrapper();
    if (!w) return;
    this.navAtFirstCol = w.scrollLeft <= 0;
    this.navAtLastCol = w.scrollLeft >= w.scrollWidth - w.clientWidth - 1;
    this.navAtFirstRow = w.scrollTop <= 0;
    this.navAtLastRow = w.scrollTop >= w.scrollHeight - w.clientHeight - 1;
    this.changeDetector.markForCheck();
  }

  private setupNavScrollListener(): void {
    this.removeNavScrollListener();
    const w = this.getTableScrollWrapper();
    if (!w) return;
    this.updateNavScrollState();
    this.navScrollListener = () => {
      this.zone.run(() => this.updateNavScrollState());
    };
    w.addEventListener('scroll', this.navScrollListener);
    const contentWrapper = w.querySelector('.cdk-virtual-scroll-content-wrapper');
    if (contentWrapper) {
      this.navContentResizeObserver = new ResizeObserver(() => {
        this.zone.run(() => this.updateNavScrollState());
      });
      this.navContentResizeObserver.observe(contentWrapper);
    }
  }

  private removeNavScrollListener(): void {
    if (this.navScrollListener) {
      const w = this.getTableScrollWrapper();
      if (w) w.removeEventListener('scroll', this.navScrollListener);
      this.navScrollListener = null;
    }
    if (this.navContentResizeObserver) {
      this.navContentResizeObserver.disconnect();
      this.navContentResizeObserver = null;
    }
  }

  onNavColOverlayShow(): void {
    setTimeout(() => this.navGoToColumnPanel?.panelElement
      ?.querySelector<HTMLElement>('.nav-goto-column-search-input')?.focus());
  }

  navColKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      if (this.navColSearchTerm) {
        this.navColSearchTerm = '';
        this.navColSelectedIndex = -1;
      } else {
        this.navGoToColumnPanel.close();
      }
      return;
    }
    const cols = this.getVisibleColumnsForNav();
    if (!cols.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.navColSelectedIndex = Math.min(this.navColSelectedIndex + 1, cols.length - 1);
      this.scrollNavColActiveIntoView();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.navColSelectedIndex = Math.max(this.navColSelectedIndex - 1, 0);
      this.scrollNavColActiveIntoView();
    } else if (event.key === 'Enter' && this.navColSelectedIndex >= 0) {
      this.navGoToColumn(cols[this.navColSelectedIndex].name);
      this.navGoToColumnPanel.close();
    }
  }

  private scrollNavColActiveIntoView(): void {
    setTimeout(() => {
      const el = document.querySelector('.nav-goto-column-item-active');
      if (el) { el.scrollIntoView({ block: 'nearest' }); }
    });
  }

  getColTablePosition(colName: string): number {
    const cols = this.displayedColumns ?? [];
    const hasOptions = cols.some(c => c.name === 'options');
    return cols.findIndex(c => c.name === colName) + 1 - (hasOptions ? 1 : 0);
  }

  getVisibleColumnsForNav(): ColumnDefinition[] {
    const term = this.navColSearchTerm.toLowerCase().trim();
    return [...(this.displayedColumns ?? [])]
      .filter(c => c.name !== 'options')
      .filter(c => !term || this.translate.instant(c.title).toLowerCase().includes(term))
      .sort((a, b) => this.translate.instant(a.title).localeCompare(this.translate.instant(b.title)));
  }

  navGoToColumn(colName: string): void {
    const w = this.getTableScrollWrapper();
    if (!w) return;
    const cols = this.displayedColumns ?? [];
    const targetIndex = cols.findIndex(c => c.name === colName);
    if (targetIndex < 0) return;
    // kolumna przypięta jest zawsze widoczna — pokaż jej naturalny kontekst od początku tabeli
    if (targetIndex < this.frozenCellsTotal) {
      w.scrollLeft = 0;
      return;
    }
    let offset = 0;
    for (let i = 0; i < targetIndex; i++)
      offset += this.getInitialColumnWidth(cols[i].name);
    // kolumna ma być pierwszą za strefą przypiętych
    w.scrollLeft = Math.max(0, Math.min(offset - this.frozenZoneWidth(), w.scrollWidth - w.clientWidth));
  }

  openPrintMenu(): void {
    this.printMenu.toggle();
  }

  printWithScope(type: PrintActions): void {
    switch (this.printScope) {
      case 'page': {
        const data = this.groupBy
          ? (this.groupedData ?? []).reduce((acc, g) => acc.concat(g.data), [] as T[])
          : this.dataSubject.value as T[];
        this.print(type, data);
        break;
      }
      case 'selection': {
        const selected = Array.isArray(this.selectedElement) ? this.selectedElement : [this.selectedElement];
        if (selected.length > 0) this.print(type, selected);
        break;
      }
      case 'all': {
        if (this.remoteDataType !== RemoteDataType.REST) break;
        this.getTransformations().pipe(first()).subscribe(transformations => {
          const filters: RequestCriteria = transformations.filters ?? { operator: Operator.and };
          const maxExportRows = this.dataSource.allowExceedMaxExportRows?.(filters)
            ? Infinity
            : this.dataSource.maxExportRows;

          this.exportService.start({
            dataLoader: this.dataLoader,
            columns: this.dataSource.getColumns(),
            transformations,
            exportType: type,
            pageSize: this.paginationSize,
            exportPageSize: this.dataSource.exportPageSize,
            maxExportRows,
            allDataPreloaded: this.allDataPreloaded
          });
        });
        break;
      }
    }
  }


  private print(type: PrintActions, data: T[]): void {
    switch (type) {
      case 'pdf': {
        this.printService.exportToPdf(data, this.dataSource.getColumns())
          .catch(error => this.toast.showErrorToast(error.toString()));
      }
        break;
      case 'xlsx': {
        this.printService.exportToExcel(data, this.dataSource.getColumns())
          .catch(error => this.toast.showErrorToast(error.toString()));
      }
        break;
      case 'csv': {
        this.printService.exportToCsv(data, this.dataSource.getColumns());
      }
        break;
    }
  }


  // ─── Minimap / Row-Ruler ────────────────────────────────────────────────
  // All pointer/resize/zoom interaction is now handled directly by
  // MinimapDirective/RowRulerDirective via template references
  // (#minimapRef="appMinimap" / #rowRulerRef="appRowRuler") — no delegation
  // methods needed on the component anymore.


  getVisibleColumnNames(): string[] {
    return this.getCurrentColumnsSettings()
      .filter((v: ColumnSetting) => v.visible === true)
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
      .map((v: ColumnSetting) => v.name);
  }

  getVisiblePrintableColumnNames(): string[] {
    const visibleColumns = this.getVisibleColumnNames();
    if (visibleColumns.length === 0 || this.dataSource?.fields == null)
      return [];
    const nameToPrintable = new Map<string, boolean>();
    this.dataSource.fields.forEach(v => nameToPrintable.set(v.column.name, v.column.printable));
    return visibleColumns.filter(name => nameToPrintable.get(name) === true);
  }

}

type PrintActions = 'pdf' | 'xlsx' | 'csv';
type PrintScope = 'page' | 'all' | 'selection';

interface FilterOverlayItem {
  field: string;
  value: any;
  active: boolean;
  appliedValue: any;
}
